// src/components/ScraperList.jsx
import React, { useState, useEffect, useMemo, useCallback, useId, useRef } from "react";
import { toast } from "sonner";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningIcon from "@mui/icons-material/Warning";
import SellIcon from "@mui/icons-material/Sell";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { v2Tone } from "../theme/theme";

import { getScraperSSEToken } from "../api/scraperService";
import { listRedirectReviews } from "../api/redirectReviews";
import { useScrapeStream } from "../hooks/useScrapeStream";
import ProgressStream from "./common/ProgressStream";
import ScraperListSkeleton from "../Skeletons/ScraperListSkeleton";
import BottomSheetMultiSelect from "./common/BottomSheetMultiSelect";
import PageContainer from "./common/PageContainer";
import ViewHeader from "./ViewHeader";
import TableFooterSimple from "./common/TableFooterSimple";
import KpiRow, { KpiCell } from "./common/KpiRow";
import StatTile from "./common/StatTile";
import { DonutCard } from "./dashboard/DashboardKit";
import CollapsibleSection from "./common/CollapsibleSection";
import Pill from "./common/Pill";
// 🔴 G10c (§2.6/§2.7): Status-`Pill` mit Text, Zeilenmenü, Rollkasten und Karte.
// 🔴 R16 B3 (Runde 1): der Karten-Titel traegt den Haus-Boden (44 px auf grobem
// Zeiger) — die Basis kommt aus dem geteilten Baustein, keine zweite Kopie.
import MobileRecordCard, { KARTE_TITEL_BASIS_SX } from "./common/MobileRecordCard";
import RowActionsMenu from "./common/RowActionsMenu";
import TableViewport from "./common/TableViewport";
import useContainerWidth from "./common/useContainerWidth";
// 🔴 G10b (§2.5/§3.2): Leiste, Pillenreihen und der EINE Query-Schreiber.
import ListToolbar from "./common/ListToolbar";
import FilterPillRow from "./common/FilterPillRow";
import EmptyState from "./common/EmptyState";
import useListQuery from "./common/listQuery/useListQuery";
import Panel from './common/Panel';
import RefreshButton from './common/RefreshButton';
import { formatDateTime, formatInt, formatMoney, formatNumber, formatPercent } from '../utils/format';
import { SP, COLUMN_MAX, TOUCH_TARGET_COARSE_QUADRAT } from '../theme/tokens';
// UI2 Teil 9, Karte Z-1c: nach der Erholung laedt die Liste selbst neu. Ihr
// Fehler-Alert ist im Ausfall E-3-ausgeblendet und kaeme sonst ohne Neuladen zurueck.
import useReloadOnRecovery from '../serviceOutage/useReloadOnRecovery';
const API = process.env.REACT_APP_API_BASE_URL || "";
const ROWS_PER_PAGE_DEFAULT = 50;
// 🔴 R16 W-3 (Nachbesserung 2): die Beschriftungsspalte der drei Analysezeilen
// braucht eine feste Mindestbreite, damit die Werte untereinander stehen. Das ist
// eine SPALTENBREITE, kein Abstand — und `check_ui_tokens.py` fuehrt `minWidth`
// in keiner Kategorie (die Luecke steht in Regel 22 §„Drei Löcher"). Deshalb
// steht die Zahl EINMAL, benannt und begruendet, statt dreimal roh. Ab `sm` gilt
// sie; unter `sm` (Telefon) nimmt die Beschriftung ihre natürliche Breite, sonst
// blieben bei 390 px von den ~330 px Panelbreite nur 130 px fuer Wert und
// Klammer, und die Klammer braeche mitten im Ausdruck um.
const ANALYSE_LABEL_MIN_WIDTH = 200;

// 🔴 G10b (§3.2): die BEDINGUNGEN als EINE Quick-Dimension. Je Wert ein
// `{value,label}` — ohne das Etikett stuende `pageGone` roh im Chip (B1.4).
// Die Reihenfolge ist die der Pillenreihe (§2.5).
const BEDINGUNGEN = [
  { value: "never", label: "Never scraped" },
  { value: "stale", label: "Stale > 24 h" },
  { value: "failing", label: "Failing 3+" },
  { value: "missing", label: "Missing price" },
  { value: "pageGone", label: "Page-gone candidates" },
];

// 🔴 Die Aktivitäts-Achse ist EINFACHAUSWAHL mit drei Zustaenden (§2.5):
// `only` (Vorgabe), `inactive`, und „ohne Einschränkung" = `active=all`.
// `all` steht NICHT in `values` — es ist der Marker, den der Hook kennt
// (`useListQuery.js`, `istMarker`); stuende es hier, waere es ein Wert wie jeder
// andere und der Vorgabe-Chip verschwaende nie.
const AKTIVITAET_WERTE = [
  { value: "only", label: "Active only" },
  { value: "inactive", label: "Inactive" },
];

// 🔴 `sort` traegt `values`, NICHT `fields` (W1: `normalisiereAchse` liest
// `values`; `fields` wuerde still ignoriert und die Werte blieben ungeprueft).
// Die sechs Spalten, die heute einen `TableSortLabel` tragen, je auf- und
// absteigend (`-feld` = absteigend, §3.1).
const SORT_VALUES = [
  "competitorName", "-competitorName",
  "productName", "-productName",
  "market", "-market",
  "totalPrice", "-totalPrice",
  "priceDifference", "-priceDifference",
  "failedScrapesInARow", "-failedScrapesInARow",
];
const SORT_VORGABE = "competitorName";
// 🔴 Eine Modulkonstante, kein `[]`-Literal im Rumpf: ein frisches Array je
// Render liesse jeden `useMemo` darunter bei JEDEM Render neu rechnen.
const LEER_LISTE = [];

/**
 * Der Spalten-Deskriptor fuer `TableViewport` — `priorities`, NICHT `columns`
 * (das sind Breiten, W3). `min`/`max` leitet der Baustein aus
 * `COLUMN_MAX[kind]` ab (`theme/tokens.js`); hier steht keine zweite Zahl.
 *
 * 🔴 §2.6: Status niedrig, Product hoch. `actions` fuehrt die Hausform von
 * `DepHealth.jsx:93` — die Spalte traegt keinen Text, `kind: 'number'` ist ihre
 * naechste Art.
 *
 * 🔴 Der Deskriptor MELDET die Tabellenbreite an den Kopf, er blendet keine
 * Spalte aus (`TableViewport.jsx:290-320`). Die Spaltenzahl im DOM bleibt die
 * des Kopfes — `messeP19`/`messeP22` lesen sie.
 *
 * 🔴 **R15 W2 (Runde 1): der Deskriptor wird aus denselben Weichen GEBAUT wie
 * die Tabelle, statt als feste Liste daneben zu stehen.** Hier stand eine
 * Modulkonstante mit acht Eintraegen, von denen einer (`priceDifference` bei
 * `showPriceDifference = false`, der Vorgabe auf `/scrapers`) nie gezeichnet
 * wurde und einer (`stockStatus`) fehlte. Folge heute klein (das Budget liegt
 * ueber dem Behaelter, `tabellenBreite = Behaelter`), aber es war eine zweite
 * Wahrheit ueber die Spalten, die bei der naechsten Aenderung falsch
 * weitergelebt haette. Der `useMemo` steht deshalb UNTEN bei den Weichen
 * (`const spalten`), nicht hier im Modulkopf.
 */

// 🔴 §2.6/§9 Q1 (Orchestrator-Entscheid A): die drei Woerter des Status-Chips.
// „Retired" ist kein neues Wort — es steht in `RedirectReviewInbox.jsx:41` und
// im Modell-Kommentar (`backend/scrapers/models.py:101`); der Auto-Retire ist
// die einzige MASCHINEN-Entscheidung auf der Zeile und wird nicht versteckt.
// Nie Farbe allein (P22): das WORT traegt die Aussage, der Grund steht im
// `title` — dieselbe Form wie `StatusDot`.
const statusWort = (s) =>
  s.isActive ? "Active" : s.inactiveReason === "page_gone_auto" ? "Retired" : "Inactive";
const statusTon = (s) =>
  s.isActive ? "ok" : s.inactiveReason === "page_gone_auto" ? "warn" : "plain";
// 🔴 R16 W5 (Runde 1): der `title` traegt nur den GRUND. Bis zur Nachbesserung
// gab die Funktion ohne `inactiveReason` das sichtbare Wort zurueck — ein
// Tooltip, der nichts hinzufuegt und den echten Grund schwerer auffindbar macht.
// Kein Grund ⇒ kein `title` (MUI laesst das Attribut dann weg).
const statusGrund = (s) =>
  s.inactiveReason ? `${statusWort(s)} — ${s.inactiveReason}` : undefined;

// 🔴 R16 B2 (Runde 1): die Zwei-Zeilen-Klemme des Kartentitels — Hausform
// `ProductsTable.TITEL_KLEMME_SX` (`frontend/src/components/ProductsTable.jsx:221`).
// Sie sitzt am INNEREN `<span>` des Links, nie am Link selbst: dessen
// `overflow: hidden` beschnitte den 44-px-Boden (`KARTE_TITEL_BASIS_SX`) auf die
// Textzeile (CSS 2.1 §11.1.1, Begruendung und Rechnung an
// `ProductsTable.KARTE_TITEL_SX`). Eine Klemme ist eine INHALTS-Entscheidung je
// Liste und steht deshalb beim Aufrufer, nicht im geteilten Baustein.
//
// 🔴 R15 W2 (Runde 2): das ist eine KOPIE der drei CSS-Zeilen, keine zweite
// Quelle derselben Zahl — und eine Kopie laeuft weg. **Aendert jemand die
// Hausform an `ProductsTable.jsx:221`, muss sie hier mitgezogen werden.** Ein
// Import von dort waere die schlechtere Kopplung (eine Seite, kein geteilter
// Baustein); ein Token waere der Ort, an dem beide Seiten es sehen, und ist
// noch nicht gebaut.
const TITEL_KLEMME_SX = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

// 🔴 R16 B1 (Runde 2) — die Spaltenbreiten sind jetzt INHALTSUNABHAENGIG.
// Runde 1 pinnte nur die Statusspalte (`STATUS_SPALTE_MIN_PX = 108`), und die
// Messung an den echten Fotos hat gezeigt, dass das nicht reicht: PRODUCT
// schrumpfte von ~406 px auf ~264 px, sobald die Datenzeilen fehlten, und das
// Auto-Layout verteilte die feste Tabellenbreite neu — MARKET wanderte um
// **−120 px**, STATUS nur um +12. Der groesste Sprung lag also NICHT in der
// gepinnten Spalte (Regel 17 §1: die Zahl trug nur ihre eigene Herleitung).
//
// Deshalb die Hausform (`ProductsTable.jsx:1030-1038`, das VORBILD — vor dieser
// Aenderung der einzige `columns`-Aufrufer im Baum, seither sind es zwei:
// `ProductsTable` und diese Datei; `POOverview.jsx:1501-1508` uebergibt nur
// `priorities` und `containerWidth`): `columns` am `TableViewport` schaltet
// `table-layout: fixed`, die Breiten kommen aus `COLUMN_MAX[kind]` als ANTEIL
// des Budgets — dieselbe Quelle, aus der der Deskriptor `spalten` schon sein
// `min`/`max` ableitet. Damit hat keine Zelle mehr Einfluss auf die
// Spaltenbreite, und die Zahl 108 entfaellt samt ihrer Begruendung. Gerechnet
// wird in `spaltenBreiten` direkt unter `spalten`.
//
// Der Drift-Waechter bleibt P33: er vergleicht die Kopfzellen-x zwischen
// befuelltem und leerem Zustand. Er faengt jetzt BEIDE Richtungen — eine
// breitere `Pill` kann die Spalte nicht mehr verschieben, eine Aenderung an
// `COLUMN_MAX` verschiebt sie in beiden Zustaenden gleich (also gruen, wie es
// sein soll).

/**
 * Der Preis als TEXT — EINE Form fuer Tabelle und Karte (§2.7/N11.3: „Werte
 * textgleich mit der Desktop-Zeile"). Ein zweites `formatMoney` auf der Karte
 * waere eine zweite Formatierung derselben Zahl.
 *
 * 🔴 Fremdwaehrung durch dieselbe Funktion: `currencyDisplay` haengt in
 * `formatMoney` an der Waehrung, der Code steht hinten (`1.234 USD`).
 * `digits: 0` ist die kompakte Form, die diese Stelle schon hatte
 * (Regel-16-Gate W2, 2026-10-06). Nicht-Zahlen geben `null` — der Aufrufer
 * entscheidet ueber den Strich.
 */
const preisText = (wert, currencyCode) =>
  typeof wert === "number"
    ? currencyCode === "EUR"
      ? formatMoney(wert)
      : formatMoney(wert, { currency: currencyCode, digits: 0 })
    : null;

// ---------- helpers ----------
function descendingComparator(a, b, orderBy) {
  const valA = a[orderBy] ?? -Infinity;
  const valB = b[orderBy] ?? -Infinity;
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  return array
    .map((el, idx) => [el, idx])
    .sort((a, b) => {
      const cmp = comparator(a[0], b[0]);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    })
    .map((el) => el[0]);
}

const StatusDot = ({ active, reason, size = 10 }) => (
  <Tooltip
    title={active ? (reason ? `Active — ${reason}` : "Active") : reason ? `Inactive — ${reason}` : "Inactive"}
    arrow
  >
    <Box
      data-testid="scraper-status-dot"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: active ? "success.main" : "grey.500",
        boxShadow: active
          ? "0 0 8px 2px rgba(46, 204, 113, 0.55)"
          : "0 0 6px 1px rgba(158,158,158,0.45)",
      }}
    />
  </Tooltip>
);

/**
 * ScraperStatusCluster — die drei Aussagen der Spalten „Status" und „Failures" in
 * einer kompakten Zeile: Aktiv-Punkt, Warnsymbol („nicht aktuell") und die
 * Fehlschlag-Anzahl.
 *
 * 🔴 Welle 2 PR 3a (2026-10-06), R16 W1/W2: in der EINGEBETTETEN Fassung fallen die
 * beiden Spalten weg (Platzgruende, siehe `showStatusCol`/`showFailuresCol` unten).
 * Bis zur Fix-Runde fiel ihr INHALT mit ihnen weg — der Kommentar behauptete, der
 * Zustandspunkt sitze „ohnehin in der Produktzelle", was nicht stimmte: `StatusDot`
 * hatte genau EINE Renderstelle, und die lag in der entfernten Spalte. Auf
 * `/products/:sku` ging so das Warnsymbol verloren, auf `CompetitorDetail`
 * zusaetzlich der Aktiv-Punkt, und eine wiederholt scheiternde Quelle war auf der
 * Wirtsseite unsichtbar. Dieser Cluster haelt alle drei Aussagen am Leben; er sitzt
 * in der ersten Zelle der Zeile (Produktzelle, oder — wenn die Produktspalte
 * kontextbedingt fehlt — in der Marktzelle).
 *
 * 🔴 **R15 W4 (Runde 1): die Vollansicht `/scrapers` behaelt ihre zwei Spalten,
 * aber NICHT unveraendert.** Bis G10c trug die Statusspalte dort einen
 * `StatusDot` (10 px, ohne Text); seit G10c traegt sie eine `Pill` mit WORT
 * („Active" / „Inactive" / „Retired") und das Warnsymbol daneben. Der Cluster
 * ist damit die EINGEBETTETE Fassung derselben drei Aussagen, nicht ein Abbild
 * der Vollansicht — hier stand bis zur Nachbesserung „unveraendert", was zum
 * falschen Schluss einlud, die beiden Fassungen saehen gleich aus.
 */
const ScraperStatusCluster = ({ s, showActiveStateDot = true, showStatusIcon = true }) => (
  <Box data-testid="scraper-status-cluster"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
    {showActiveStateDot && <StatusDot active={s.isActive} reason={s.inactiveReason} size={8} />}
    {showStatusIcon && s.lastScraped && !s.isUpToDate && (
      <Tooltip title={`Last Scraped: ${formatDateTime(s.lastScraped)}`}>
        <WarningIcon color="warning" fontSize="small" />
      </Tooltip>
    )}
    {s.failedScrapesInARow > 0 ? (
      <Tooltip
        title={`${s.failedScrapesInARow} consecutive failures · ${s.failedScrapesTotal} in total`}
      >
        <Box component="span">
          <Pill tone="err">{s.failedScrapesInARow}× failed</Pill>
        </Box>
      </Tooltip>
    ) : s.failedScrapesTotal > 0 ? (
      <Typography
        variant="caption" color="text.secondary"
        title={`${s.failedScrapesTotal} failures in total`}
      >
        {s.failedScrapesTotal}×
      </Typography>
    ) : null}
  </Box>
);

/**
 * Props:
 * - variant: 'page' | 'embedded'
 * - footerMode: 'hybrid' | 'inline' | 'none' | 'auto'
 * - showFilters: boolean (only used for page variant)
 */
export default function ScraperList({
  initialProductFilter = "",
  initialCompetitorFilter = "",
  showFilters = true,
  showActiveStateDot = true,
  showStatusIcon = true,
  showPriceDifference = false,
  showStockStatus = false,
  greyOutInactive = false,

  variant = "page",
  footerMode = "auto",
  showBreadcrumbs: showBreadcrumbsProp,
  rowsPerPageInitial = ROWS_PER_PAGE_DEFAULT,
  addButtonInEmbedded = false,
  onAddButtonClick,
  addButtonLabel = "Add new scraper",
  tableOnly = false,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEmbedded = variant === "embedded";
  const showBreadcrumbs = isEmbedded ? false : showBreadcrumbsProp ?? true;
  const renderFilters = !isEmbedded && showFilters && !tableOnly;

  // 🔴 Befund 3d (Welle 2): in der EINGEBETTETEN Fassung traegt die Tabelle
  // weniger Spalten als die Vollansicht auf `/scrapers`. Mit neun Spalten
  // (`showPriceDifference` + `showStockStatus` gesetzt) brauchte sie ~1290 px,
  // stand auf `/products/:sku` aber in einer ~1150-px-Inhaltsbox — der Ueberhang
  // war nur ueber einen waagerechten Bildlauf erreichbar, den macOS nicht
  // sichtbar anzeigt, und die rechte „Actions"-Spalte stand abgeschnitten.
  //
  // Die Regel ist NICHT „dieselben vier Spalten ueberall", sondern: die Spalte,
  // die der Kontext der Wirtsseite schon FESTLEGT, faellt weg. Auf `/products/:sku`
  // ist die Liste auf EIN Produkt gefiltert (`initialProductFilter`) — die
  // Produktspalte wiederholte in jeder Zeile denselben Namen. In
  // `CompetitorDetail` ist sie auf EINEN Konkurrenten gefiltert
  // (`initialCompetitorFilter`) — dort faellt die Konkurrentenspalte, die
  // Produktspalte bleibt (sonst waere die Zeile nicht mehr zuzuordnen).
  // Dazu fallen im eingebetteten Zustand zwei Nebenspalten weg: „Status"
  // (Zustandspunkt + Warnsymbol) und „Failures". Beide bleiben auf `/scrapers`
  // erhalten.
  //
  // 🔴 Ihr INHALT geht NICHT verloren (R16 W1/W2, Welle 2 PR 3a). Hier stand bis
  // zur Fix-Runde, der Zustandspunkt sitze „ohnehin in der Produktzelle, siehe
  // `showActiveStateDot`" — das war FALSCH: `StatusDot` hatte genau eine
  // Renderstelle, und die lag in der entfernten Statusspalte. Auf `/products/:sku`
  // fiel damit das Warnsymbol („nicht aktuell") weg, auf `CompetitorDetail`
  // zusaetzlich der Aktiv-Punkt, und eine wiederholt scheiternde Quelle war auf
  // der Wirtsseite unsichtbar. Der `ScraperStatusCluster` traegt die drei Aussagen
  // jetzt in der ERSTEN Zelle der Zeile (Produktzelle, sonst Marktzelle) — er wird
  // an genau EINER Stelle je Zeile gerendert, damit nichts doppelt erscheint.
  const scopedProduct = isEmbedded && !!initialProductFilter;
  const scopedCompetitor = isEmbedded && !!initialCompetitorFilter;
  const showStatusCol = !isEmbedded;
  // 🔴 §2.1/B9: `seitenModus` ist die EINE Weiche, die den Seitenbaum von der
  // eingebetteten Fassung trennt — der `table`-Baum ist GETEILTER Kode
  // (`embeddedSection` rendert denselben Baum), und `tableOnly` (die Fassung
  // ohne Panel) zaehlt wie eingebettet: dort gibt es weder Karten noch
  // `TableViewport`.
  const seitenModus = !isEmbedded && !tableOnly;
  const showProductCol = !scopedProduct;
  const showCompetitorCol = !scopedCompetitor;
  const showFailuresCol = !isEmbedded;
  // 🔴 R15 W2 (Runde 1): der Spalten-Deskriptor, aus DENSELBEN Weichen gebaut,
  // die den Kopf zeichnen (Begruendung im Modulkopf an der entfernten
  // `SPALTEN`-Konstante). Reihenfolge und `priority`/`kind` sind unveraendert —
  // neu ist nur, dass eine Spalte, die diese Fassung nicht rendert, auch nicht
  // im Budget steht.
  const spalten = useMemo(
    () => [
      ...(showStatusCol ? [{ key: "status", priority: "lg", kind: "chip" }] : []),
      ...(showProductCol ? [{ key: "product", priority: "kern", kind: "identity" }] : []),
      { key: "market", priority: "kern", kind: "text" },
      ...(showCompetitorCol ? [{ key: "competitor", priority: "lg", kind: "text" }] : []),
      { key: "lastPrice", priority: "kern", kind: "number" },
      ...(showPriceDifference ? [{ key: "priceDifference", priority: "lg", kind: "number" }] : []),
      ...(showFailuresCol ? [{ key: "failures", priority: "lg", kind: "chip" }] : []),
      ...(showStockStatus ? [{ key: "stockStatus", priority: "lg", kind: "chip" }] : []),
      { key: "actions", priority: "kern", kind: "number" },
    ],
    [
      showStatusCol,
      showProductCol,
      showCompetitorCol,
      showPriceDifference,
      showFailuresCol,
      showStockStatus,
    ],
  );
  // 🔴 R16 B1 (Runde 2): die BREITEN der gezeichneten Spalten, als Anteil des
  // Budgets `Σ COLUMN_MAX[kind].max` — Hausform `ProductsTable.jsx:1030-1038`.
  // Sie gehen als `columns` an den `TableViewport`, der damit
  // `table-layout: fixed` einschaltet. Die Reihenfolge IST die DOM-Reihenfolge
  // (`columns[i]` setzt `th:nth-of-type(i+1)`, `TableViewport.jsx:616-627`), und
  // `spalten` wird aus denselben Weichen gebaut wie der Kopf — deshalb passen
  // beide Listen ohne eigene Zuordnung.
  //
  // ⚠️ Nur der Seitenmodus rendert den `TableViewport`; die eingebettete Fassung
  // und `tableOnly` bekommen diese Breiten NIE zu sehen (B9). Der `useMemo`
  // steht trotzdem unbedingt, wie `spalten` daneben — kein Hook hinter einem
  // `return`.
  const spaltenBreiten = useMemo(() => {
    const budget = spalten.reduce((summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0), 0);
    if (budget <= 0) return null;
    return spalten.map((sp) => ({
      width: `${((COLUMN_MAX[sp.kind]?.max ?? 0) / budget) * 100}%`,
    }));
  }, [spalten]);
  const normalizeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean).map(String);
    return [String(val)];
  };

  // catalogs
  const [markets, setMarkets] = useState([]);
  const marketsById = useMemo(() => Object.fromEntries(markets.map((m) => [m.id, m])), [markets]);
  const marketsByCode = useMemo(
    () => Object.fromEntries(markets.map((m) => [String(m.code || "").toUpperCase(), m])),
    [markets]
  );
  const marketsByName = useMemo(
    () => Object.fromEntries(markets.map((m) => [String(m.name || "").toLowerCase(), m])),
    [markets]
  );
  const renderMarket = useCallback(
    (raw) => {
      if (!raw) return "—";
      const found =
        marketsById[raw] ||
        marketsByCode[String(raw).toUpperCase()] ||
        marketsByName[String(raw).toLowerCase()];
      // `raw` was returned here, so a market the lookup could not resolve
      // printed its raw code — including an invalid ISO code like `SU` for
      // Switzerland. An unresolved market is unknown, not raw.
      return found ? `${found.name} (${found.code})` : "—";
    },
    [marketsByCode, marketsById, marketsByName]
  );

  // data
  const [rawConfigs, setRawConfigs] = useState(null);
  // Set when any of the three dashboard fetches fails. Distinguishes "still
  // loading" from "will never load" — without it a failed /api/scrapers/config
  // leaves rawConfigs at null and the skeleton renders forever.
  const [loadError, setLoadError] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const competitorNameMap = useMemo(
    () => Object.fromEntries((competitors || []).map((c) => [String(c.id), c.name || c.id])),
    [competitors]
  );

  // 🔴 G10b (§3.2): **EIN Schreiber** — `useListQuery`. Die fünf Dimensionen,
  // die Sortierung und die Seitengroesse liegen in der ADRESSE; `q` deckt
  // Produkt, Variante und SKU (der `product`-Parameter ist entfallen, §3.1).
  // Die eingebettete Fassung (`/products/:sku`) behaelt ihren lokalen
  // 0-basierten Seitenzustand und ihre Scope-Props — sie rendert keine Leiste
  // und schreibt deshalb nie in die Adresse ihres Wirts (B9).
  // 🔴 Jede Dimension traegt `label` und `values` (B1) — ohne `label` wirft der
  // Hook, ohne `values` akzeptierte `leseWert` JEDEN Wert und P13 koennte nie
  // gruen werden. Die EINE Ausnahme ist `q`: Freitext hat keine geschlossene
  // Werteliste (Hausform `POOverview.jsx`, `SecondLifeBestand.jsx`).
  const dimensions = useMemo(
    () => [
      { key: "q", kind: "single", label: "Search" },
      {
        key: "competitor",
        kind: "multi",
        label: "Competitors",
        values: (competitors || []).map((c) => ({ value: String(c.id), label: c.name || String(c.id) })),
      },
      {
        key: "market",
        kind: "multi",
        label: "Markets",
        values: (markets || []).map((m) => ({ value: String(m.id), label: `${m.name} (${m.code})` })),
      },
      // EINE Achse, drei Zustaende; `all` steht NICHT in `values` (Marker, §2.5).
      { key: "active", kind: "quick", label: "Active", values: AKTIVITAET_WERTE, default: ["only"] },
      { key: "quick", kind: "quick", label: "Conditions", values: BEDINGUNGEN },
    ],
    [competitors, markets],
  );

  const query = useListQuery({
    // 🔴 B9: die EINGEBETTETE Fassung bekommt KEINE Dimensionen — sie rendert
    // keine Leiste und darf die Adresse ihrer Wirtsseite (`/products/:sku`)
    // nicht pruefen, bereinigen oder beschreiben. Ihre Einengung kommt aus den
    // Props (`initialProductFilter`, `initialCompetitorFilter`).
    dimensions: isEmbedded ? LEER_LISTE : dimensions,
    // 🔴 B1 (Regel-15 Runde 1): auch `sort` bleibt der EINGEBETTETEN Fassung fremd.
    // Sie sortiert lokal (`embeddedSort` unten), schreibt `sort` nicht in die
    // Adresse ihrer Wirtsseite — und prueft dort auch kein fremdes `?sort=`:
    // ohne die Achse ist `sortierung.values` leer, `leseAchse` meldet nichts und
    // der Bereinigungs-Effekt des Hooks loescht keinen Wirts-Parameter (§1.3).
    sort: isEmbedded ? undefined : { default: SORT_VORGABE, values: SORT_VALUES },
    pageSize: ROWS_PER_PAGE_DEFAULT,
  });
  const { state, set, clearAll, chips, ignoredValues } = query;

  // Suchtext: in der Seite die Dimension `q`, eingebettet immer leer (B9).
  const searchText = isEmbedded ? "" : state.q ?? "";
  // 🔴 Die Aktivitäts-Achse ist EINFACHAUSWAHL mit drei Zustaenden (§2.5):
  // `state.active` ist ein Array (`kind: 'quick'`), `null`/leer heisst
  // „ohne Einschränkung" (`active=all`). 🔴 Die eingebettete Fassung hat keine
  // Dimensionen (B9) und behaelt ihre heutige Vorgabe „nur aktive".
  const aktiverModus = isEmbedded
    ? "only"
    : !state.active || state.active.length === 0
    ? "all"
    : state.active[0];
  // Bedingungen — UND-verknuepft, alle auf demselben `quick`-Parameter.
  const quickWerte = state.quick ?? LEER_LISTE;
  // Sortierung: `feld` aufsteigend, `-feld` absteigend (§3.1).
  // 🔴 B1 (Regel-15 Runde 1): die EINGEBETTETE Fassung sortiert LOKAL — genau die
  // Form, die vor diesem PR galt (§1.3: „unveraendert"). Ein Klick auf einen
  // sortierbaren Kopf auf `/products/:sku` darf weder `?sort=` in die Wirts-URL
  // schreiben noch ein fremdes `?sort=` der Wirtsseite bereinigen.
  const [embeddedSort, setEmbeddedSort] = useState(SORT_VORGABE);
  const sortWert = isEmbedded ? embeddedSort : state.sort || SORT_VORGABE;
  const sortAbsteigend = sortWert.startsWith("-");
  const sortFeld = sortAbsteigend ? sortWert.slice(1) : sortWert;
  const order = sortAbsteigend ? "desc" : "asc";
  const orderBy = sortFeld;

  const [embeddedPage, setEmbeddedPage] = useState(0);
  // 🔴 Die Seitengroesse bleibt lokal (kein Umschalter in dieser Ansicht, §3.1);
  // die eingebettete Fassung behaelt ihren Umschalter.
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageInitial);
  // 🔴 UI2 Teil 10, G10a: `trackedProducts` startet als `null` (UNBEKANNT), nicht
  // als `[]` („keine Produkte"). `[]` hiesse „null Produkte ohne Scraper" und
  // damit eine 0, wo die Zahl noch nicht geladen ist (Regel 22 Komponentenregel 3,
  // „unbekannt ist nie 0"). Der `.catch` setzt ebenfalls `null`; EIN Verbraucher
  // traegt hier einen `?? []`-Guard (Absturzschutz) — der zweite kommt mit den
  // Quick-Pillen in G10b, und erst dann steht „zwei" in `docs/features/scraper.md`.
  // Die ANGEZEIGTE Zahl bleibt `—`, solange der Zustand `null` ist (W36/B16).
  const [trackedProducts, setTrackedProducts] = useState(null);
  // 🔴 R16 B-1 (Nachbesserung 2): `null` allein trug ZWEI Bedeutungen — „laedt
  // noch" und „Abruf gescheitert" — und beide renderten denselben Satz („has not
  // loaded yet") ohne Ausweg. Der Ladezustand steht deshalb daneben, DREIWERTIG:
  // `laedt` · `fehler` · `ok`. `trackedProducts` bleibt die Liste selbst (`null`
  // ausser bei `ok`) — die ANGEZEIGTE Zahl ist in beiden unbekannten Faellen `—`,
  // und genau das ist richtig. Der Unterschied liegt im GRUND und im Ausweg.
  const [productLoad, setProductLoad] = useState("laedt");
  // Wiederhol-Zaehler: `Try again` zieht den Produktabruf mit, indem es ihn
  // hochzaehlt (er steht in den Abhaengigkeiten des Effekts). Ein Zaehler statt
  // eines Funktionszeigers, weil der Effekt sonst bei jedem Render neu liefe.
  const [productReload, setProductReload] = useState(0);
  // 🔴 G10a-Nachbesserung (B-2): der Dialog traegt KEIN `known` mehr. Er ist nur
  // erreichbar, wenn der Bestand bekannt ist (der Knopf ist sonst per
  // `aria-disabled`/`onClick`-Guard gesperrt, `uncoveredCount === null`), also war
  // der dritte Zweig „Product list has not loaded yet." im Dialog toter Kode
  // (Regel 15 Runde 2, W-3). Der Leerzweig „Every tracked product already has a
  // scraper target." ist damit eine Aussage ueber einen GELADENEN Bestand.
  const [missingSkuModal, setMissingSkuModal] = useState({ open: false, items: [] });
  // Der Sperrgrund des Knopfes „Products without scrapers" steht GENAU EINMAL
  // sichtbar im DOM; der Knopf zeigt per `aria-describedby` darauf (Hausform
  // `pages/secondLife/OpenCases.jsx:703-717`, `DisabledReason` §@dontUseFor).
  const missingSkuReasonId = useId();

  // menus / dialogs / toasts
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [confirmOpenState, setConfirmOpenState] = useState(false);
  const [actionId, setActionId] = useState(null);

  // inline scrape dialog
  const [scrapeDialog, setScrapeDialog] = useState({ open: false, target: null });
  const scrape = useScrapeStream();
  // Depend on the STABLE stop fn (useCallback([]) in useScrapeStream), NOT the
  // `scrape` object — that object is a fresh literal every render, so [scrape]
  // fires this cleanup on EVERY render and closes the EventSource the same tick
  // start() opens it (aborting the request before it's even sent). Mount/unmount only.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- exhaustive-deps wants [scrape], but scrape is a fresh object each render → that reintroduces the every-render cleanup bug; scrape.stop is stable (useCallback([]))
  useEffect(() => () => scrape.stop(), [scrape.stop]);
  const [maintenanceDialog, setMaintenanceDialog] = useState({ open: false, mode: "preview", summary: null });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [dedupeConfirmOpen, setDedupeConfirmOpen] = useState(false);
  const [competitorModalOpen, setCompetitorModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 🔴 W30 (§2.6): `TableViewport` braucht die ROHE Behaelterbreite — ohne sie
  // faellt es auf die Musterbreite zurueck (bei `width="full"` sind das 1280,
  // `CONTENT_WIDTH.full` traegt `null`). Der `ref` sitzt am TABELLENKASTEN,
  // nicht am `TableViewport`: gemessen wird der RAUM, nicht die gedeckelte
  // Tabelle (Hausform `POOverview.jsx:1503-1521`).
  const tabelleRef = useRef(null);
  const behaelterBreite = useContainerWidth(tabelleRef);

  // Redirect-review pending badge (Task 8) — links to /scrapers/redirect-reviews.
  // null while unloaded so the chip stays hidden instead of flashing "(0)".
  // On fetch error the chip still renders (degraded "(?)" + tooltip) rather
  // than vanishing: this chip is the route's only entry point (R16 W6).
  const [pendingReviewCount, setPendingReviewCount] = useState(null);
  const [pendingReviewError, setPendingReviewError] = useState(false);
  useEffect(() => {
    if (isEmbedded) return;
    listRedirectReviews("pending")
      .then((d) => {
        setPendingReviewError(false);
        setPendingReviewCount(d?.counts?.pending ?? 0);
      })
      .catch(() => setPendingReviewError(true));
  }, [isEmbedded]);

  // 🔴 **Der EINE Migrationsweg** (G5 §3.2a, Hausform `RMAOverviewLayout.jsx`):
  // die alte Adressform wird beim Aufbau EINMAL kanonisiert. Er ist kein
  // zweiter Schreiber der Dimensionen — `useListQuery` bleibt der einzige —,
  // sondern die Uebernahme der zwei Alt-Formen (§3.2):
  //   · `pageGone=1` (Mass-Event-Mail, `notify/scraper_checks.py`) →
  //     `quick=pageGone` **und** `active=all` (inaktive Ziele sind per
  //     Definition betroffen — ohne das landete der Deep-Link auf einer leeren
  //     Tabelle, der am 2026-08-02 reparierte Fehler), `pageGone` wird
  //     **geloescht** (B12: der Hook kennt den Parameter nicht, er ueberlebte
  //     sonst jeden `set`, `clearAll` und Reload);
  //   · `active=only,inactive` (getippt; die Einfachauswahl kann es nicht
  //     erzeugen, W35) → der ERSTE Wert gewinnt, sonst zeigte die Pille „only",
  //     waehrend das Praedikat beide Werte saehe.
  // 🔴 W4/C2 (Regel-15 Runde 2): die Deklarationsreihenfolge allein traegt NICHT.
  // `useListQuery`s Bereinigungs-Effekt haengt auch an `gemeldet.url`; sein
  // `setGemeldet` loest einen zweiten Lauf aus, der mit dem Render-Stand VOR dem
  // `replace` des Hooks noch die alte Adresse sieht — und damit nach der
  // Migration schreibt. Gemessen (C2): die Migration schrieb `quick=pageGone`,
  // der Hook ueberschrieb sie mit der nur teilweise bereinigten Adresse.
  //
  // Deshalb ist der Effekt an `searchParams` gebunden und laeuft, bis die Adresse
  // kanonisch ist: er ist idempotent (kein `geaendert` ⇒ kein Schreiben), und
  // nach dem letzten Schreibvorgang faellt `geaendert` weg. Das konvergiert,
  // statt auf eine Reihenfolge zu bauen, die niemand zusichert.
  useEffect(() => {
    if (isEmbedded) return;
    const p = new URLSearchParams(searchParams);
    // 🔴 W3 (Regel-15 Runde 1): die Bereinigung des Hooks laeuft in DEMSELBEN
    // Durchlauf, aber ihr `replace` ist beim Aufbau noch nicht im Render-Stand
    // angekommen — `searchParams` hier ist die ALTE Adresse. Ohne diese Zeilen
    // nähme die Migration einen gerade verworfenen Wert (`quick=bogus`) als
    // Grundlage und schriebe ihn zurueck; der Hinweis erschiene erneut. Dieselbe
    // Korrektur wie im Hook (`useListQuery.js`, Bereinigungs-Effekt).
    ignoredValues.forEach((e) => {
      if (e.rest == null || e.rest === "") p.delete(e.param);
      else p.set(e.param, e.rest);
    });
    let geaendert = false;
    const pageGoneParam = p.get("pageGone");
    if (pageGoneParam != null) {
      if (pageGoneParam === "1") {
        const quick = new Set((p.get("quick") || "").split(",").filter(Boolean));
        quick.add("pageGone");
        p.set("quick", Array.from(quick).join(","));
        if (!p.get("active")) p.set("active", "all");
      }
      p.delete("pageGone");
      geaendert = true;
    }
    const activeParam = p.get("active");
    if (activeParam && activeParam.includes(",")) {
      p.set("active", activeParam.split(",")[0]);
      geaendert = true;
    }
    // 🔴 Nur schreiben, wenn sich die Adresse WIRKLICH aendert — sonst liefe der
    // Effekt nach jedem eigenen `replace` erneut und schriebe dieselbe Adresse.
    if (geaendert && p.toString() !== searchParams.toString()) {
      setSearchParams(p, { replace: true });
    }
  }, [isEmbedded, searchParams, ignoredValues, setSearchParams]);

  // fetch
  const fetchConfigs = useCallback(() => {
    setLoadError(false);
    const params = new URLSearchParams();
    // 🔴 UI2 Teil 10, G10a (§4.2, W11): die SEITE sendet keinen `sku` mehr — sie
    // laedt den vollen Bestand, weil die KPI auf dem UNGEFILTERTEN Bestand rechnet
    // (N6.2). Der Produktfilter wirkt clientseitig (`effectiveProductFilter` in
    // `filtered`). Serverseitig eingeengt wird nur noch die EINGEBETTETE Fassung
    // (`/products/:sku`) — dort ist die Einengung der Sinn der Einbettung.
    // 🔴 W11 (Regel-15 Runde 2): der Rückfall auf die Marke `ALL_PRODUCTS`
    // ist seit Runde 1 entfallen (die Achse wird eingebettet gar nicht mehr
    // übergeben), kein Aufrufer setzt sie noch. Ein leerer Wert heisst
    // „kein Parameter" — genau wie vorher `sku === ALL_PRODUCTS`.
    const sku = isEmbedded ? initialProductFilter : null;
    if (sku) params.append("sku", sku);
    const qs = params.toString();

    const fail = (label) => (err) => {
      console.error(`Error loading ${label}:`, err);
      setLoadError(true);
    };
    // 🔴 G10a (W36/P10): ein HTTP-Fehler ist ein FEHLER, kein leeres Ergebnis.
    // `fetch` verwirft bei 4xx/5xx nicht — ein JSON-Fehlerkoerper haette
    // `setRawConfigs({error: …})` gesetzt, und `rawConfigs.map` waere beim
    // naechsten Render gestorben (weisse Seite). Mit dem `ok`-Check landet er
    // im `.catch`, `loadError` wird gesetzt, und der Fehlerzweig rendert —
    // keine Kachel, keine 0.
    // 🔴 `=== false`, nicht `!r.ok`: ein echtes `Response` traegt `ok` immer,
    // die Jest-Doubles der Bestandsproben nicht — ein fehlendes `ok` ist keine
    // Fehlerantwort und darf die Suite nicht rot faerben.
    // Der Aufruf bleibt bewusst DIREKT am `fetch` (kein Helfer): das
    // Endpunkt-Register erkennt die Aufrufer an dieser Form.
    const p1 = fetch(`${API}/api/scrapers/config?${qs}`)
      .then((r) => {
        if (r.ok === false) throw new Error(`scraper configs: HTTP ${r.status}`);
        return r.json();
      })
      .then(setRawConfigs)
      .catch(fail("scraper configs"));
    const p2 = fetch(`${API}/api/scrapers/competitors`)
      .then((r) => {
        if (r.ok === false) throw new Error(`competitors: HTTP ${r.status}`);
        return r.json();
      })
      .then(setCompetitors)
      .catch(fail("competitors"));
    const p3 = fetch(`${API}/api/markets`)
      .then((r) => {
        if (r.ok === false) throw new Error(`markets: HTTP ${r.status}`);
        return r.json();
      })
      .then(setMarkets)
      .catch(fail("markets"));

    return Promise.all([p1, p2, p3]);
    // 🔴 W11/B13: der Produktfilter steht ABSICHTLICH nicht in der Liste. Die
    // Seite sendet `sku` nicht mehr (die KPI rechnet auf dem Bestand), der Filter
    // wirkt clientseitig — eine Abhaengigkeit wuerde bei jeder Produktwahl drei
    // Requests neu ausloesen, ohne dass der Server anders antwortet. In der
    // EINBETTUNG kommt der `sku` aus `initialProductFilter` (B9: es gibt keinen
    // eigenen Filterzustand mehr) und aendert sich nicht. Wer ihn je dynamisch
    // macht, traegt die Dep BEGRUENDET wieder ein (dann mit `isEmbedded` zusammen).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded]);
  // 🔴 R16 B-1: „Try again" zieht den PRODUKTABRUF mit. Vorher heilte es nur die
  // Ziel-Liste — nach einem gescheiterten Produktabruf blieb der Knopf „Products
  // without scrapers" fuer den Rest der Seitenlebensdauer gesperrt, und der
  // einzige Ausweg war ein Neuladen der Seite. Beide Aufrufer (der Fehler-`Alert`
  // der Ziel-Liste und der Grund am gesperrten Knopf) gehen ueber diesen EINEN
  // Weg, damit sie nicht auseinanderlaufen.
  const retryAll = useCallback(() => {
    fetchConfigs();
    setProductReload((n) => n + 1);
  }, [fetchConfigs]);
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // 🔴 UI2 Teil 9, Karte Z-1c (E-3): der BESTEHENDE Ladeweg, kein zweiter.
  useReloadOnRecovery(fetchConfigs);

  useEffect(() => {
    if (isEmbedded || tableOnly) return undefined;
    // 🔴 R16 B-1: jeder Lauf — der erste wie ein Wiederholen — beginnt sichtbar
    // als `laedt`. Der Abbruch-Flag haelt die Antwort eines ueberholten Laufs
    // davon ab, den neueren Zustand zu ueberschreiben (zwei Klicks auf „Try
    // again" hintereinander).
    let abgebrochen = false;
    setProductLoad("laedt");
    fetch(`${API}/api/products?status=active`)
      // 🔴 W36: auch hier ist ein HTTP-Fehler UNBEKANNT, nicht „keine Produkte".
      // Ohne diese Zeile liefert ein 500 mit JSON-Koerper `[]` — und die
      // Zusammenfassung schriebe eine 0, wo die Zahl nicht bekannt ist.
      .then((r) => {
        // `=== false`, s. `hole()` oben: ein fehlendes `ok` (Jest-Double) ist
        // keine Fehlerantwort.
        if (r.ok === false) throw new Error(`products: HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (abgebrochen) return;
        const items = Array.isArray(data)
          ? data
              .filter((p) => {
                const rawStatus =
                  p.status ??
                  p.workflow_status ??
                  p.product_status ??
                  p.state ??
                  null;
                const status = rawStatus ? String(rawStatus).trim().toLowerCase() : "";
                if (status) {
                  if (
                    status.includes("archive") ||
                    status.includes("draft") ||
                    status.includes("inactive") ||
                    status.includes("disabled")
                  ) {
                    return false;
                  }
                  if (["active", "live", "published", "enabled"].includes(status)) {
                    return true;
                  }
                }
                if ("is_active" in p) return Boolean(p.is_active);
                if ("active" in p) return Boolean(p.active);
                if ("enabled" in p) return Boolean(p.enabled);
                if ("archived" in p && p.archived) return false;
                if ("draft" in p && p.draft) return false;
                return true;
              })
              .map((p) => ({
                sku: p.sku,
                name: p.product_name || p.title || p.sku,
                status:
                  p.status ||
                  p.workflow_status ||
                  p.product_status ||
                  p.state ||
                  "",
              }))
          : [];
        setTrackedProducts(items);
        setProductLoad("ok");
      })
      // 🔴 W36: ein gescheiterter Abruf heisst UNBEKANNT, nicht „keine Produkte".
      // `[]` waere eine 0 in der Zusammenfassung; `null` rendert `—`.
      // 🔴 R16 B-1: der Zustand `fehler` unterscheidet ihn von „laedt" — nur so
      // kann der Grund den WAHREN Satz sagen und ein Wiederholen angeboten werden.
      .catch(() => {
        if (abgebrochen) return;
        setTrackedProducts(null);
        setProductLoad("fehler");
      });
    return () => {
      abgebrochen = true;
    };
  }, [isEmbedded, tableOnly, productReload]);

  // 🔴 N10.3: jede Abfrageaenderung setzt die Seite auf 1 — das erledigt
  // `useListQuery` selbst. Der frueher hier stehende `useEffect(() =>
  // setPage(0), …)` entfaellt damit ersatzlos (Hausform `SecondLifeBestand`).

  // derive rows
  const scrapers = useMemo(() => {
    if (!rawConfigs) return [];
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return rawConfigs.map((s) => {
      const hasPrice = typeof s.last_price === "number";
      const shipping = s.last_shipping ?? 0;
      const lastScrapedDate = s.last_scraped_at ? new Date(s.last_scraped_at) : null;
      const totalPrice = hasPrice ? s.last_price + shipping : undefined;
      const ourPrice = s.our_base_price;
      const difference = ourPrice != null && totalPrice != null ? ourPrice - totalPrice : null;

      return {
        id: s.id,
        sku: s.sku,
        productName: s.product_name ?? "—",
        productVariant: s.variant_name ?? "—",
        market: s.market_id ?? "—",
        marketCode: s.market_code ?? null,
        competitorId: s.competitor_id,
        competitorName: s.competitor_name ?? "—",
        lastPrice: s.last_price,
        lastShipping: s.last_shipping,
        totalPrice,
        priceDifference: difference,
        ourBasePrice: ourPrice,
        lastScraped: lastScrapedDate,
        hasSale: Boolean(s.last_is_sale),
        availability: s.last_availability,
        isUpToDate: lastScrapedDate ? lastScrapedDate.getTime() > twentyFourHoursAgo : false,
        isActive: s.active,
        inactiveReason: s.inactive_reason || null,
        pageGoneStreak: s.page_gone_streak ?? 0,
        failedScrapesTotal: s.failed_scrapes_total ?? 0,
        failedScrapesInARow: s.failed_scrapes_in_a_row ?? 0,
        productUrl: s.product_url ?? null,
      };
    });
  }, [rawConfigs]);

  const productOptions = useMemo(() => {
    const map = new Map();
    (rawConfigs || []).forEach((s) => {
      if (!map.has(s.sku)) {
        map.set(s.sku, { id: s.sku, name: `${s.product_name} — ${s.variant_name}` });
      }
    });
    return Array.from(map.values());
  }, [rawConfigs]);
  const productNameMap = useMemo(
    () => Object.fromEntries(productOptions.map((p) => [p.id, p.name])),
    [productOptions]
  );

  const marketOptions = useMemo(
    () =>
      (markets || []).map((m) => ({
        ...m,
        label: `${m.name} (${m.code})`,
      })),
    [markets]
  );
  // 🔴 Die drei `en-US`-Formatierer sind am 2026-10-06 gefallen (Regel-16-Gate
  // W2): `numberFormatter` → `formatInt`, `decimalFormatter` → `formatNumber`,
  // `percentFormatter` → `formatPercent` — eine Form, aus `format.js`. Der
  // Waechter sah sie nicht: `Intl.NumberFormat("en-US")` traegt ein
  // Locale-LITERAL, und die drei alten Muster zaehlen nur den fehlenden Locale.

  // filtering / sorting / paging
  // 🔴 Die EINGEBETTETE Fassung ist auf EIN Produkt und hoechstens EINEN
  // Wettbewerber eingeengt — das kommt aus den Props, nicht aus der Adresse
  // ihres Wirts (B9). Die Seitenfassung liest die Dimensionen.
  const embeddedCompetitors = useMemo(
    () => normalizeArray(initialCompetitorFilter),
    [initialCompetitorFilter],
  );
  const effectiveProductFilter =
    isEmbedded && initialProductFilter && initialProductFilter !== "" ? initialProductFilter : "";
  const effectiveCompetitorFilter = isEmbedded
    ? embeddedCompetitors
    : state.competitor ?? LEER_LISTE;
  const effectiveMarketFilter = isEmbedded ? LEER_LISTE : state.market ?? LEER_LISTE;

  // Prefill fuer „Add new scraper": in der Seite die SKU, wenn die Suche GENAU
  // eine bekannte SKU ist (der `product`-Parameter ist entfallen, §3.1).
  const singleProductPrefill = isEmbedded
    ? effectiveProductFilter || null
    : productNameMap[searchText.trim()]
    ? searchText.trim()
    : null;
  const singleCompetitorPrefill =
    effectiveCompetitorFilter.length === 1 ? effectiveCompetitorFilter[0] : null;
  const singleMarketPrefill =
    effectiveMarketFilter.length === 1 ? effectiveMarketFilter[0] : null;

  const buildCreateUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (singleProductPrefill) {
      params.set("sku", singleProductPrefill);
      const productLabel = productNameMap[singleProductPrefill];
      if (productLabel) params.set("product_label", productLabel);
    }
    if (singleCompetitorPrefill) {
      params.set("competitor", singleCompetitorPrefill);
      const competitorLabel = competitorNameMap[singleCompetitorPrefill];
      if (competitorLabel) params.set("competitor_label", competitorLabel);
    }
    if (singleMarketPrefill) {
      params.set("market_id", singleMarketPrefill);
      const marketMeta = marketOptions.find((m) => String(m.id) === String(singleMarketPrefill));
      if (marketMeta?.code) params.set("market_code", marketMeta.code);
    }
    const query = params.toString();
    return `/scrapers/new${query ? `?${query}` : ""}`;
  }, [
    singleProductPrefill,
    singleCompetitorPrefill,
    singleMarketPrefill,
    productNameMap,
    competitorNameMap,
    marketOptions,
  ]);

  const handleAddScraper = useCallback(() => {
    navigate(buildCreateUrl());
  }, [navigate, buildCreateUrl]);

  // 🔴 Die fuenf Bedingungen (§2.5) — UND-verknuepft, auf dem UNGEFILTERTEN
  // Bestand gezaehlt. `stale` traegt `lastScraped` ausdruecklich mit (B8):
  // `isUpToDate` ist bei fehlendem `lastScraped` false, sonst zaehlten die nie
  // gescrapten Ziele mit.
  const bedingungen = useMemo(
    () => ({
      never: (s) => !s.lastScraped,
      stale: (s) => s.isActive && s.lastScraped && !s.isUpToDate,
      failing: (s) => (s.failedScrapesInARow ?? 0) >= 3,
      missing: (s) => s.lastScraped && typeof s.totalPrice !== "number",
      pageGone: (s) => s.pageGoneStreak > 0 || s.inactiveReason === "page_gone_auto",
    }),
    [],
  );

  const filtered = useMemo(() => {
    const suchbegriff = searchText.trim().toLowerCase();
    const base = scrapers
      .filter((s) => {
        if (isEmbedded) return true; // no search in embedded
        if (!suchbegriff) return true;
        const hay = `${s.productName} ${s.productVariant} (${s.sku})`.toLowerCase();
        return hay.includes(suchbegriff);
      })
      .filter((s) => (effectiveCompetitorFilter.length === 0 || effectiveCompetitorFilter.includes(String(s.competitorId))))
      .filter((s) => (!effectiveProductFilter || s.sku === effectiveProductFilter))
      .filter((s) => {
        if (effectiveMarketFilter.length === 0) return true;
        return effectiveMarketFilter.some((marketId) => {
          const m = marketsById[marketId];
          if (!m) return false;
          const raw = s.market;
          return (
            String(raw) === String(m.id) ||
            String(raw).toUpperCase() === String(m.code || "").toUpperCase() ||
            String(raw).toLowerCase() === String(m.name || "").toLowerCase()
          );
        });
      })
      // Die Aktivitaets-Achse: `only` (Vorgabe), `inactive`, `all` (leer).
      .filter((s) =>
        aktiverModus === "only" ? s.isActive === true : aktiverModus === "inactive" ? !s.isActive : true,
      )
      .filter((s) => quickWerte.every((wert) => !bedingungen[wert] || bedingungen[wert](s)));

    return base;
  }, [
    scrapers,
    searchText,
    effectiveCompetitorFilter,
    effectiveProductFilter,
    effectiveMarketFilter,
    aktiverModus,
    quickWerte,
    bedingungen,
    marketsById,
    isEmbedded,
  ]);

  const dashboard = useMemo(() => {
    if (!scrapers.length) return null;
    const totalTargets = scrapers.length;
    const activeTargets = scrapers.filter((s) => s.isActive).length;
    const inactiveTargets = totalTargets - activeTargets;
    const skuMap = new Map();
    const marketCoverageMap = new Map();
    const neverScraped = [];
    const missingPriceTargets = [];

    scrapers.forEach((s) => {
      if (!skuMap.has(s.sku)) {
        skuMap.set(s.sku, { count: 0 });
      }
      skuMap.get(s.sku).count += 1;

      const marketKey = s.market || "—";
      if (!marketCoverageMap.has(marketKey)) {
        marketCoverageMap.set(marketKey, {
          key: marketKey,
          targets: 0,
          activeTargets: 0,
          skuSet: new Set(),
        });
      }
      const coverageEntry = marketCoverageMap.get(marketKey);
      coverageEntry.targets += 1;
      if (s.isActive) coverageEntry.activeTargets += 1;
      coverageEntry.skuSet.add(s.sku);

      if (!s.lastScraped) {
        neverScraped.push(s);
      }

      if (s.lastScraped && typeof s.totalPrice !== "number") {
        missingPriceTargets.push(s);
      }
    });
    // 🔴 B8: „Stale" sind AKTIVE, schon einmal gescrapte Ziele ohne frischen Lauf.
    // `isUpToDate` ist bei fehlendem `lastScraped` false (`:484`), deshalb traegt
    // das Praedikat `lastScraped` ausdruecklich mit — sonst zaehlten die nie
    // gescrapten Ziele mit (9 gemessen gegen 14). Dieselbe Formel steht in der
    // Prod-Abfrage (§4.3) und spaeter an der Quick-Pille (§2.5).
    const staleTargets = scrapers.filter((s) => s.isActive && s.lastScraped && !s.isUpToDate);
    const failureStreakTargets = scrapers.filter((s) => (s.failedScrapesInARow ?? 0) >= 3);
    const failureStreakTotal = failureStreakTargets.reduce(
      (sum, s) => sum + (s.failedScrapesTotal ?? 0),
      0
    );

    const totalSkus = skuMap.size;
    const skuWithThreePlus = Array.from(skuMap.values()).filter((entry) => entry.count >= 3).length;
    const avgTargets = totalSkus ? totalTargets / totalSkus : 0;
    const distribution = [
      { name: "1 target", value: 0 },
      { name: "2 targets", value: 0 },
      { name: "3 targets", value: 0 },
      { name: "4+ targets", value: 0 },
    ];
    skuMap.forEach((entry) => {
      if (entry.count >= 4) distribution[3].value += 1;
      else distribution[Math.max(0, entry.count - 1)].value += 1;
    });

    const coverage = Array.from(marketCoverageMap.values())
      .map((entry) => ({
        market: entry.key,
        label: renderMarket(entry.key),
        skuCount: entry.skuSet.size,
        targetCount: entry.targets,
        activeTargetCount: entry.activeTargets,
      }))
      .sort((a, b) => b.skuCount - a.skuCount);

    const marketsWithCoverage = coverage.filter((c) => c.skuCount > 0).length;
    const marketsWithThreePlus = coverage.filter((c) => c.targetCount >= 3).length;

    // 🔴 §2.3 (N6.2): EINE Reihe aus VIER Kacheln, alle aus dem UNGEFILTERTEN
    // Bestand (`scrapers`, nicht `filtered`). Die sechs Zahlen, die dadurch die
    // Leiste verlassen, stehen an benannten Orten: Active/Inactive und
    // Missing price in der `summary` der Analyseflaeche (§2.4, bis G10b dort,
    // danach an den Quick-Pillen), Markets tracked / Avg targets / Products ≥3
    // als drei Textzeilen in der Analyseflaeche.
    // 🔴 N6.1: keine Kachel ist klickbar — kein `onClick`, kein Hover, kein
    // Tooltip, der die eigene Liste einengt. Die Bedingungen filtern ueber
    // Quick-Pillen (G10b), nicht ueber Kacheln.
    const kpiTiles = [
      {
        label: "Targets",
        value: formatInt(totalTargets),
        secondary: `${formatInt(activeTargets)} active`,
        hint: "All scraper configs saved in the system.",
      },
      {
        label: "Never scraped",
        value: formatInt(neverScraped.length),
        secondary: "no persisted price",
        hint: "Targets that never produced a persisted price entry.",
      },
      {
        label: "Stale > 24 h",
        value: formatInt(staleTargets.length),
        secondary: `of ${formatInt(activeTargets)} active`,
        hint: "Active targets whose last successful scrape is older than 24 hours.",
      },
      {
        label: "Failing 3+",
        value: formatInt(failureStreakTargets.length),
        secondary: `${formatInt(failureStreakTotal)} total failures`,
        hint: "Targets that failed three or more times in a row.",
      },
    ];

    // 🔴 B16: `trackedProducts` ist `null`, bis der Abruf antwortet — der Guard
    // verhindert nur den Absturz (`null.filter` waere ein TypeError beim ERSTEN
    // Render, weisse Seite). Die ZAHL bleibt davon unberuehrt: sie ist `null`,
    // solange der Zustand unbekannt ist (R4-W1 unten), NICHT `uncovered.length`.
    const skuUniverse = new Set(Array.from(skuMap.keys()));
    const uncovered = (trackedProducts ?? []).filter((p) => !skuUniverse.has(p.sku));

    return {
      kpiTiles,
      distribution,
      coverage,
      totalSkus,
      // 🔴 R4-W1: die Zahl „Products without scrapers" hat einen BENANNTEN
      // Rueckgabewert — nicht `alerts.find(…).count ?? 0`, das im `null`-Fall
      // die verbotene 0 liefert (Regel 22 Komponentenregel 3).
      uncoveredCount: trackedProducts === null ? null : uncovered.length,
      uncovered,
      inactiveCount: Math.max(0, inactiveTargets),
      missingPriceCount: missingPriceTargets.length,
      // 🔴 G10b (§2.5): die Zaehler der beiden Pillenreihen — der BESTAND, nicht
      // die Treffermenge (N6.2). Sonst zeigte die Pille nach dem ersten Filter
      // eine Zahl, die von der eigenen Auswahl abhaengt.
      pillenZahlen: {
        active: activeTargets,
        inactive: inactiveTargets,
        never: neverScraped.length,
        stale: staleTargets.length,
        failing: failureStreakTargets.length,
        missing: missingPriceTargets.length,
        pageGone: scrapers.filter(
          (s) => s.pageGoneStreak > 0 || s.inactiveReason === "page_gone_auto",
        ).length,
      },
      marketsWithCoverage,
      marketsWithThreePlus,
      avgTargets,
      skuWithThreePlus,
    };
  }, [scrapers, renderMarket, trackedProducts]);

  const sorted = useMemo(() => stableSort(filtered, getComparator(order, orderBy)), [filtered, order, orderBy]);

  // 🔴 B3 — der Hook fuehrt `page` 1-BASIERT, `TableFooterSimple` 0-basiert. Die
  // Umrechnung steht EINMAL hier im JSX, nicht im Hook; die Formel ist dieselbe
  // wie `maxSeite` (`pages/secondLife/bestand.js:266`, W37) und wird hier nicht
  // importiert, sondern als EINE Zeile gefuehrt.
  const letzteSeite = Math.max(0, Math.ceil(sorted.length / (rowsPerPage || 1)) - 1);
  const seiteAktiv = isEmbedded
    ? embeddedPage
    : Math.min(Math.max(0, (Number(state.page) || 1) - 1), letzteSeite);

  const pageRows = useMemo(() => {
    if (!rowsPerPage || rowsPerPage <= 0) return sorted;
    const start = seiteAktiv * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, seiteAktiv, rowsPerPage]);

  // Befund 3d: die Zahl folgt den tatsaechlich gezeichneten Spalten (die
  // eingebettete Fassung laesst Status / Produkt bzw. Konkurrent / Failures weg).
  const columnCount =
    (showStatusCol ? 1 : 0) +
    (showProductCol ? 1 : 0) +
    1 + // Market
    (showCompetitorCol ? 1 : 0) +
    1 + // Last Price
    (showFailuresCol ? 1 : 0) +
    1 + // Actions
    (showPriceDifference ? 1 : 0) +
    (showStockStatus ? 1 : 0);
  const needsPagination = rowsPerPage > 0 && sorted.length > rowsPerPage;
  const resolvedFooterMode = footerMode === "auto" ? (isEmbedded ? "inline" : "hybrid") : footerMode;
  // 🔴 §2.10: genau eine Spalte traegt `aria-sort` — der Klick schreibt die
  // Sortier-DIMENSION (`-feld` = absteigend). Die Reihenfolge der Schnittmenge
  // bleibt davon unberuehrt (P18). 🔴 Ausnahme ist die EINGEBETTETE Fassung
  // (B1): sie hat keine Dimension und fuehrt den Sortierzustand lokal.
  const handleRequestSort = (prop) => {
    const isAsc = orderBy === prop && order === "asc";
    const naechster = isAsc ? `-${prop}` : prop;
    // 🔴 B1 (Regel-15 Runde 1): eingebettet bleibt die Sortierung im Bauteil.
    // Der Kopf-Klick dort ist KEINE Adressaenderung der Wirtsseite (B9).
    if (isEmbedded) {
      setEmbeddedSort(naechster);
      return;
    }
    set("sort", naechster);
  };

  const openMenu = (e, id) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(id);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  // actions
  const activate = (id) => {
    closeMenu();
    fetch(`${API}/api/scrapers/config/${id}/activate`, { method: "POST" })
      .then((r) => {
        if (!r.ok) throw new Error("Activate failed");
        toast.success("Target activated.");
        fetchConfigs();
      })
      .catch((err) => toast.error(err.message));
  };
  const deactivateAsk = (id) => {
    closeMenu();
    setActionId(id);
    setConfirmOpenState(true);
  };
  const deactivate = () => {
    if (!actionId) return;
    fetch(`${API}/api/scrapers/config/${actionId}/deactivate`, { method: "POST" })
      .then((r) => {
        if (!r.ok) throw new Error("Deactivate failed");
        toast.success("Target deactivated.");
        fetchConfigs();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => {
        setConfirmOpenState(false);
        setActionId(null);
      });
  };

  const scrapeAndPersist = async (target) => {
    closeMenu();
    setScrapeDialog({ open: true, target });

    // EventSource can't send Authorization headers — fetch SSE token first.
    let sseToken;
    try {
      sseToken = await getScraperSSEToken();
    } catch (err) {
      return;
    }

    const params = new URLSearchParams({
      target_id: target.id,
      product_url: target.productUrl || '',
      persist: 'true',
      token: sseToken,
    });
    scrape.start({
      url: `${API}/api/scrapers/config/run?${params.toString()}`,
      onClose: fetchConfigs,
    });
  };

  const closeScrapeDialog = () => {
    scrape.stop();
    setScrapeDialog({ open: false, target: null });
  };

  const handlePreviewDuplicates = async () => {
    setMaintenanceLoading(true);
    try {
      const res = await fetch(`${API}/api/scrapers/maintenance/duplicates`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Preview failed");
      }
      const data = await res.json();
      setMaintenanceDialog({ open: true, mode: "preview", summary: data });
    } catch (err) {
      toast.error(err.message || "Failed to load duplicates");
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const runDedupe = async () => {
    setDedupeConfirmOpen(false);
    setMaintenanceLoading(true);
    try {
      const res = await fetch(`${API}/api/scrapers/maintenance/dedupe_targets`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Deduplication failed");
      }
      setMaintenanceDialog({ open: true, mode: "dedupe", summary: data });
      toast.success(`Deduplicated ${data.total_groups ?? 0} duplicate groups`);
      fetchConfigs();
    } catch (err) {
      toast.error(err.message || "Deduplication failed");
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const closeMaintenanceDialog = () => setMaintenanceDialog({ open: false, mode: "preview", summary: null });

  // 🔴 R16 C1 (2026-10-06): der Kopf steht VOR dem Lade-Zweig (nicht eingebettet) und
  // wird in JEDEM Zustand gerendert. Vorher kehrte der Ladezustand in ein Skelett
  // zurueck, das nur graue Balken trug — und die mobile Leiste schweigt seit 5(i):
  // im Laden stand GAR KEIN Titel. Den Titel trug zuerst nur der geladene Zweig.
  // Seit der Nachbesserung 1 (R15 B-2) traegt der Ladezweig auch die AKTIONEN —
  // dieselben, deaktiviert wo sie Daten brauchen.

  // ── Die Aktionszeile des Kopfes — fuer BEIDE Zustaende DIESELBE ──────────
  //
  // 🔴 Welle 2 PR 3b, Nachbesserung 1 (2026-10-06), R15 B-2. Bis hierher trug
  // der Ladezweig `actionsPending`: einen 44-px-Platzhalter, der GENAU EINE
  // Zeile reserviert. Geladen standen hier aber DREI Aktionen plus der
  // `rightAdornment`-Block; auf Mobil standen damals ALLE inline, und bei
  // 390 px brachen die fuenf Pillen auf DREI Zeilen um. Der Kopf wuchs damit
  // beim Eintreffen der Daten um ~114 px — die Reservierung war eine
  // Schaetzung, keine Aussage.
  //
  // Jetzt rendert der Ladezweig die ECHTEN Aktionen, deaktiviert wo sie Daten
  // brauchen. Die Zeile ist damit exakt so hoch wie geladen, und die
  // Beschriftungen stehen von Anfang an da statt beim Laden zu fehlen.
  // 🔴 Seit E5 (2026-10-07) steht mobil ohnehin nur noch die primaere Aktion
  // inline — die zwei Warteschlangen-Eingaenge wohnen im ⋯-Ueberlauf. Die
  // Zusicherung „Laden so hoch wie geladen" gilt unveraendert, weil beide
  // Zustaende DIESELBEN Aktions- und Ueberlauf-Arrays bekommen.
  const laedtNoch = rawConfigs === null || competitors === null;

  const headerActions = !isEmbedded
    ? [
        {
          key: "add",
          label: addButtonLabel,
          // 🔴 E2 (UI-Durchgang 2, 2026-10-07): die EINE gefuellte Aktion des
          // Kopfes. `emphasis` macht das ausdruecklich — ohne den Schluessel
          // traegt nur der Rueckfall („die erste Aktion") die Rangfolge, und
          // eine Umsortierung kippte sie still.
          emphasis: "primary",
          // Reine Navigation (`buildCreateUrl`) — braucht keine der beiden
          // Listen und bleibt deshalb auch im Laden bedienbar.
          onClick: handleAddScraper,
          startIcon: <AddIcon fontSize="small" />,
        },
        {
          // 🔴 T3-13 (UI-Durchgang 2 Teil 3, 2026-10-08): die zwei Knoepfe sind
          // EIN Paar — „Preview duplicates" mit „Deduplicate" als Variante
          // (§5 Nr. 3).
          // 🔴 W17 — die Reihenfolge ist GETAUSCHT, und das ist die
          // Entscheidung: die zerstörende Hälfte wird NICHT der Hauptknopf. Sie
          // trägt `color: 'warning'` und öffnet eine eigene Rückfrage; als
          // Hauptknopf wäre sie der lauteste Klick der Seite (Warnfarbe neben
          // dem primary „Add New Scraper") und läge ohne Menüöffnung unter dem
          // Zeiger. Die lesende Hälfte ist der Hauptknopf, die zerstörende steht
          // im Menü — dieselbe Richtung wie F1 („Archive Done" geht in den ⋯).
          // Der Hauptknopf trägt damit `outlined` (E2-Rückfall: keine
          // ausdrückliche Variante an einer nicht-primären Aktion) und die
          // Farbe SEINER Aktion; `warning` bleibt am Menüeintrag.
          // 🔴 KM-1 (2026-10-10): **hier steht KEIN `SplitButton` mehr.** Diese
          // Seite hat einen ⋯ („Price Checker", „Redirect Reviews"), und ein
          // Seitenkopf zeigt höchstens EIN Menü. Der Kopf rendert „Preview
          // duplicates" deshalb als einfachen Knopf und stellt „Deduplicate"
          // OBEN in den ⋯ — auf dem Desktop als oberster Eintrag ohne
          // Einrückung, mobil (wo der Knopf fehlt) eingerückt unter dem Namen
          // des Hauptknopfes. Die W17-Entscheidung darüber ist unberührt: die
          // zerstörende Hälfte ist NICHT der Hauptknopf und behält ihre
          // Warnfarbe (`color: 'warning'` geht auf beiden Wegen mit).
          key: "preview",
          label: "Preview duplicates",
          onClick: handlePreviewDuplicates,
          // Der Dedupe-Lauf liest die Ziele — ohne sie gibt es nichts zu
          // pruefen. 🔴 W9: beide Hälften tragen DENSELBEN `disabled` — keine
          // Hälfte kann halb sperren. 🔴 KM-1: im ⋯ trägt der Kopf diese Summe
          // selbst auf den Varianteneintrag auf (`u.disabled || a.disabled ||
          // a.loading || a.running`), mobil wie auf dem Desktop.
          disabled: maintenanceLoading || laedtNoch,
          menu: [{
            // Destruktiv (deaktiviert Ziele, schreibt Preise um — der
            // Bestätigungsdialog bestätigt mit `color="error"`). Die Warnfarbe
            // geht mit: das Paar wandert in den ⋯-Überlauf (mobil als
            // eingerückter Untereintrag, auf dem Desktop als oberster Eintrag),
            // und an BEIDEN Stellen rendert `ViewHeader` den Eintrag MIT
            // `color` (B2, §5 Nr. 1; KM-1).
            key: "dedupe",
            label: "Deduplicate",
            color: "warning",
            onClick: () => setDedupeConfirmOpen(true),
            disabled: maintenanceLoading || laedtNoch,
          }],
        },
      ]
    : [];

  // 🔴 Der Zaehler „Redirect Reviews (n)" kam bis hierher erst mit der
  // Nebenabfrage. Fehlte er im Ladezweig, war die Zeile eine Pille kuerzer und
  // brach anders um — dieselbe Hoehe, aber ein anderer Umbruchpunkt. Er steht
  // jetzt immer: „(…)" solange die Zahl laeuft, „(?)" im Fehlerfall (R16 W6 —
  // dieser Eintrag ist der einzige Eingang in die Warteschlange und darf nicht
  // verschwinden), sonst die Zahl.
  const redirectReviewsLabel = pendingReviewError
    ? "Redirect Reviews (?)"
    : pendingReviewCount != null
      ? `Redirect Reviews (${pendingReviewCount})`
      : "Redirect Reviews (…)";

  // Die zwei Warteschlangen-Eingaenge — bis 2026-10-07 standen sie als
  // `rightAdornment` neben den Aktionsknoepfen (fuenf Knoepfe in einer Ecke).
  // Sie sind Navigation, keine Aktion der Liste, und wohnen jetzt im
  // ⋯-Ueberlauf (E1). 🔴 Der Eingang zum naechtlichen Preis-Pruefer (E5a-2)
  // bleibt damit erreichbar — ohne ihn ist die Liste nur ueber eine
  // eingetippte URL zu erreichen, und eine Flaeche, die niemand findet,
  // erreicht ebenso wenig jemanden wie ein Signal ohne Flaeche (Regel-16-Gate).
  // Bewusst OHNE Zaehler: der Endpunkt liefert keinen.
  const headerOverflow = !isEmbedded
    ? [
        {
          key: "price-checker",
          label: "Price Checker",
          onClick: () => navigate("/scrapers/watch-findings"),
        },
        {
          key: "redirect-reviews",
          label: redirectReviewsLabel,
          // R16 W6: der Grund stand als Tooltip am Knopf; im Menue traegt ihn
          // der native `title` des Eintrags.
          tooltip: pendingReviewError
            ? "Count could not be loaded, open the queue anyway"
            : undefined,
          onClick: () => navigate("/scrapers/redirect-reviews"),
        },
      ]
    : [];

  // Der Lauf-Anzeiger bleibt ein Anzeiger — E1 nimmt nur KNOEPFE aus dem
  // `rightAdornment`, kein Lebenszeichen.
  const headerRightAdornment = !isEmbedded && maintenanceLoading
    ? <CircularProgress size={18} />
    : null;

  // 🔴 §2.8: der Fehler des ERSTEN Aufbaus ist KEIN Ladezustand — er rendert
  // Leiste (gesperrt), Sperrhinweis und den `error`-Leerzustand im Tabellen-
  // koerper. Deshalb steht `!loadError` in der Bedingung; ohne ihn liefe die
  // Seite nach einem gescheiterten Abruf fuer immer im Skelett.
  if (!loadError && (rawConfigs === null || competitors === null)) {
    // 🔴 Welle 2 PR 3b, Nachbesserung 1 (2026-10-06): der Ladezweig stand OHNE
    // `PageContainer` da — der Kopf trug seinen Seitenrand selbst
    // (`ViewHeader` liest `PageContainerContext`; ohne Container liefert er
    // `px: PAGE_GUTTER`), waehrend das Skelett seinen eigenen `PageContainer`
    // mitbrachte. Gemessen bei 390 px sass der Titel dadurch im Laden bei
    // x = 12 px und geladen bei x = 28 px — ein waagerechter Sprung von 16 px
    // genau in dem Moment, in dem die Daten eintreffen.
    // Jetzt traegt EIN `PageContainer` Kopf UND Inhalt, genau wie im geladenen
    // Zweig. Der verschachtelte `PageContainer` des Skeletts erkennt das
    // (`PageContainerContext`) und laesst seinen eigenen Puffer weg — es gibt
    // also keinen doppelten Rand.
    // 🔴 R15 Runde 3, W-3: das Skelett reserviert Fusszeile, mobile Zahlenzeile
    // und Analyse-Kopf nur, wenn der GELADENE Zweig sie zeigt — dieselbe
    // Bedingung (`!isEmbedded && !tableOnly`), hier durchgereicht.
    if (isEmbedded) return <ScraperListSkeleton isEmbedded />;
    return (
      <PageContainer width="full">
        <Box data-testid="liste-kopf">
          <ViewHeader
            title="Scraper Targets"
            breadcrumbs={showBreadcrumbs ? [{ label: "Home", href: "/" }, { label: "Scraper Targets" }] : []}
            // R15 B-2: die ECHTEN Aktionen statt `actionsPending`. Sie sind hier
            // deaktiviert, wo sie Daten brauchen (`laedtNoch`).
            actions={headerActions}
            overflow={headerOverflow}
            rightAdornment={headerRightAdornment}
          />
        </Box>
        <ScraperListSkeleton tableOnly={tableOnly} />
      </PageContainer>
    );
  }

  // 🔴 §2.8 Sonderfall: die Vorgabe „nur aktive" ist schuld an der leeren
  // Treffermenge, und es GIBT inaktive Ziele — dann nennt der Leerzustand den
  // Grund und bietet den Weg heraus. Dieselbe Lage deckt „Active only ∧
  // Page-gone" (§2.5) ab: dort steht der Grund in den Bedingungen.
  // 🔴 §2.8: der Fehler des ERSTEN Aufbaus sperrt jede Eingabe (N8.5) — die
  // Leiste, beide Pillenreihen und der Oeffner tragen `disabled`, „Clear all"
  // bleibt bedienbar. Er steht VOR dem Tabellenbaum, weil der Leerzustand im
  // `tbody` ihn liest.
  const sperre = rawConfigs === null && loadError;

  const vorgabeFiltertAlles =
    sorted.length === 0 && scrapers.length > 0 && aktiverModus === "only" && scrapers.some((s) => !s.isActive);

  // 🔴 §2.7: EIN Aktionssatz, zwei Traeger — die Zeile und die Karte zeigen
  // dasselbe Menue („⋮ mit demselben Menue"). §1.3: alle fuenf Handlungen
  // bleiben; die zwei Linkeintraege navigieren jetzt ueber `navigate` (das
  // Menue ist kein Anker mehr) und „Open product URL" oeffnet wie bisher ein
  // neues Fenster.
  const zeilenAktionen = (s) => [
    {
      key: "scrape",
      label: "Scrape & persist",
      icon: <PlayArrowIcon fontSize="small" />,
      onClick: () => scrapeAndPersist(s),
    },
    {
      key: "edit",
      label: "Edit scraper config",
      onClick: () => navigate(`/scrapers/${s.id}/edit`),
    },
    {
      key: "product-url",
      label: "Open product URL",
      icon: <OpenInNewIcon fontSize="small" />,
      hidden: !s.productUrl,
      onClick: () => window.open(s.productUrl, "_blank", "noopener,noreferrer"),
    },
    { key: "product", label: "View product", onClick: () => navigate(`/products/${s.sku}`) },
    s.isActive
      ? { key: "deactivate", label: "Deactivate…", onClick: () => deactivateAsk(s.id) }
      : { key: "activate", label: "Activate", onClick: () => activate(s.id) },
  ];
  // 🔴 B5/P19: der zugängliche Name der Zeilenaktion traegt den ganzen
  // Zeilenbezug — beide Textknoten der Kennungsspalte.
  const zeilenAktionsName = (s) =>
    `Actions for ${s.productName} — ${s.productVariant} (${s.sku})`;

  // 🔴 §2.8: „kein Ziel ueberhaupt" und „kein Treffer" sind ZWEI Objekte mit
  // eigenem Grund (`data-reason`, P9) — nicht derselbe Satz. Der Leerzustand
  // steht am Rechner IM Tabellenkoerper (W13/P33, ueber alle Spalten); auf dem
  // Telefon gibt es keinen Tabellenkoerper, dort traegt ihn die Kartenzone —
  // derselbe Knoten, EIN Objekt (G10c).
  const leerzustand = sperre ? (
    // 🔴 §2.8: der Fehler des ERSTEN Aufbaus hat einen eigenen
    // Grund (`error`), nicht „nichts da" — mit einem Weg heraus.
    <EmptyState
      reason="error"
      message="Scraper targets could not be loaded."
      onRetry={retryAll}
    />
  ) : scrapers.length === 0 ? (
    <EmptyState
      reason="no-data"
      message="No scraper targets yet."
      detail="Add a target to start tracking competitor prices."
      action={
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon fontSize="small" />}
          onClick={handleAddScraper}
        >
          Add new scraper
        </Button>
      }
    />
  ) : (
    <EmptyState
      reason="no-match"
      noun="target"
      query={searchText.trim() || undefined}
      searchedIn="Searched in product, variant and SKU."
      // 🔴 C1 (Regel-15 Runde 2): `clearAll` schreibt `tab`,
      // `sort` UND `page` — eingebettet ist das die Adresse der
      // WIRTSseite. Die eingebettete Fassung hat keine Filter zu
      // bereinigen und bekommt deshalb keine Handlung.
      onClearFilters={isEmbedded ? undefined : clearAll}
      // 🔴 W2 (Regel-15 Runde 1): `query` ohne Weg heraus ist der
      // Zustand, gegen den `EmptyState` selbst warnt (E.2) — die
      // Suche wird hier geleert, nicht nur alle Filter.
      onClearSearch={searchText.trim() ? () => set("q", null) : undefined}
      // 🔴 Der Sonderfall Vorgabe (§2.8): sind alle AKTIVEN Ziele
      // weggefiltert und es gibt inaktive, sagt der Text das und
      // bietet „Show all targets" — der einzige Weg aus der
      // Vorgabe, den die Achse selbst nicht hat (§2.5).
      // 🔴 W2: auch der NICHT-Vorgabe-Fall traegt seine Hauptzeile.
      // Ohne sie faellt `EmptyState` auf seinen deutschen Vorgabetext
      // („Nichts passt zu dieser Auswahl.") auf einer englischen Seite.
      // 🔴 C1: der Knopf setzt `state.active`, das die eingebettete
      // Fassung nicht liest — er wäre ein sichtbarer Knopf ohne
      // Wirkung, und `set` schriebe in die Wirtsadresse.
      message={
        vorgabeFiltertAlles
          ? isEmbedded
            ? "All matching targets are inactive."
            : "No active target matches these filters — inactive targets match."
          : "No target matches these filters."
      }
      action={
        vorgabeFiltertAlles && !isEmbedded ? (
          <Button size="small" variant="outlined" onClick={() => set("active", [])}>
            Show all targets
          </Button>
        ) : undefined
      }
    />
  );

  const table = (
    <Table
      size="small"
      sx={{
        // Befund 3d: die eingebettete Fassung ist schmaler (Wirtsseite) UND
        // traegt weniger Spalten — 960 war die Breite der Vollansicht.
        minWidth: isEmbedded ? 520 : 960,
        borderCollapse: "separate",
        "& thead th": {
          bgcolor: (theme) => theme.palette.grey[100],
          borderBottom: "1px solid",
          borderColor: "divider",
          whiteSpace: "nowrap",
          lineHeight: 1.4,
          fontSize: 12,
        },
        "& .MuiTableCell-root": {
          py: 1,
          px: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <TableHead>
        <TableRow>
          {showStatusCol && (
            // 🔴 R16 B1 (Runde 2): hier steht KEINE eigene Breite mehr. Die
            // Breiten kommen als `columns` an den `TableViewport`
            // (`spaltenBreiten`) und gelten fuer ALLE sieben Spalten gleich —
            // die Runde-1-Zahl `STATUS_SPALTE_MIN_PX` pinnte nur diese eine und
            // liess MARKET um 120 px springen. `display` bleibt: unter `md`
            // traegt die Karte den Status, und die Spalte muss auch aus dem Kopf
            // verschwinden, sonst zaehlt P33 sie mit.
            <TableCell
              align="center"
              sx={{ display: { xs: 'none', md: 'table-cell' } }}
            >
              Status
            </TableCell>
          )}
          {showProductCol && (
            <TableCell sortDirection={orderBy === "productName" ? order : false}>
              <TableSortLabel
                active={orderBy === "productName"}
                direction={orderBy === "productName" ? order : "asc"}
                onClick={() => handleRequestSort("productName")}
              >
                Product / Variant / SKU
              </TableSortLabel>
            </TableCell>
          )}
          <TableCell sortDirection={orderBy === "market" ? order : false}>
            <TableSortLabel
              active={orderBy === "market"}
              direction={orderBy === "market" ? order : "asc"}
              onClick={() => handleRequestSort("market")}
            >
              Market
            </TableSortLabel>
          </TableCell>
          {showCompetitorCol && (
            <TableCell sortDirection={orderBy === "competitorName" ? order : false}>
              <TableSortLabel
                active={orderBy === "competitorName"}
                direction={orderBy === "competitorName" ? order : "asc"}
                onClick={() => handleRequestSort("competitorName")}
              >
                Competitor
              </TableSortLabel>
            </TableCell>
          )}
          <TableCell align="right" sortDirection={orderBy === "totalPrice" ? order : false}>
            <TableSortLabel
              active={orderBy === "totalPrice"}
              direction={orderBy === "totalPrice" ? order : "asc"}
              onClick={() => handleRequestSort("totalPrice")}
            >
              Last Price
            </TableSortLabel>
          </TableCell>
          {showPriceDifference && (
            <TableCell align="right" sortDirection={orderBy === "priceDifference" ? order : false}>
              <TableSortLabel
                active={orderBy === "priceDifference"}
                direction={orderBy === "priceDifference" ? order : "asc"}
                onClick={() => handleRequestSort("priceDifference")}
              >
                Diff. to Us
              </TableSortLabel>
            </TableCell>
          )}
          {showFailuresCol && (
            <TableCell align="right" sortDirection={orderBy === "failedScrapesInARow" ? order : false}>
              <TableSortLabel
                active={orderBy === "failedScrapesInARow"}
                direction={orderBy === "failedScrapesInARow" ? order : "asc"}
                onClick={() => handleRequestSort("failedScrapesInARow")}
              >
                Failures
              </TableSortLabel>
            </TableCell>
          )}
          {showStockStatus && <TableCell>Stock Status</TableCell>}
          <TableCell align="center">Actions</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {pageRows.map((s) => {
          const currencyCode = marketsByCode[s.marketCode?.toUpperCase()]?.currency_code ?? "EUR";
          // 🔴 Eine Form fuer Zeile UND Karte (`preisText`, Modulkopf): die
          // Karte muss textgleich mit der Zeile sein (§2.7/N11.3).
          const fmtCompPrice = (v) => preisText(v, currencyCode);
          const tooltipTitle = (
            <Box>
              {`Our Price: ${formatMoney(s.ourBasePrice)}`}<br />
              {typeof s.lastPrice === "number" && `Comp. Price: ${fmtCompPrice(s.lastPrice)}`}<br />
              {`Comp. Shipping: ${fmtCompPrice(s.lastShipping ?? 0)}`}<br />
              {s.lastScraped && `Scraped: ${formatDateTime(s.lastScraped)}`}
            </Box>
          );
          // R16 W1/W2: nur in der eingebetteten Fassung — dort fehlen die Spalten
          // „Status" und „Failures", und dieser Cluster haelt ihren Inhalt. Er
          // landet in der ersten Zelle der Zeile: in der Produktzelle, wenn sie
          // gerendert wird, sonst in der Marktzelle (siehe unten).
          const embeddedStatus = isEmbedded ? (
            <ScraperStatusCluster
              s={s}
              showActiveStateDot={showActiveStateDot}
              showStatusIcon={showStatusIcon}
            />
          ) : null;
          return (
            <TableRow
              key={s.id}
              hover
              sx={{ ...(greyOutInactive && !s.isActive ? { opacity: 0.5 } : {}) }}
            >
              {showStatusCol && (
                // 🔴 R15 B1: `whiteSpace: nowrap` — sonst koennte ein Umbruch die
                // Spaltenbreite erneut vom Inhalt abhaengig machen und die feste
                // Untergrenze am Kopf umgehen.
                <TableCell
                  align="center"
                  sx={{ whiteSpace: "nowrap", display: { xs: 'none', md: 'table-cell' } }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    {/* 🔴 §2.6/P22: PUNKT → `Pill` MIT TEXT (Regel 22
                        Komponentenregel 1 — EIN Chip-Bauteil, nicht der
                        MUI-`Chip`). Das WORT traegt die Aussage, der Grund
                        steht im `title`. Das Warnsymbol („nicht aktuell")
                        bleibt daneben: es ist eine ZWEITE Aussage und faellt
                        mit dem Punkt nicht weg (§1.3 — jede Bestandsfunktion
                        behaelt ihren Ort). */}
                    <Pill tone={statusTon(s)} title={statusGrund(s)}>
                      {statusWort(s)}
                    </Pill>
                    {showStatusIcon && s.lastScraped && !s.isUpToDate && (
                      <Tooltip title={`Last Scraped: ${formatDateTime(s.lastScraped)}`}>
                        <WarningIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              )}

              {showProductCol && (
                <TableCell>
                  {/* R16 W1/W2: erste Zelle der Zeile, wenn die Produktspalte
                      gerendert wird (Vollansicht UND eingebettet auf
                      `CompetitorDetail`). `embeddedStatus` ist in der Vollansicht
                      `null` — der Wrapper aendert dort nichts. */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    {embeddedStatus}
                    <Box sx={{ minWidth: 0 }}>
                      <MuiLink component={RouterLink} to={`/scrapers/${s.id}/edit`} underline="hover">
                        {s.productName} — {s.productVariant}
                      </MuiLink>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ({s.sku})
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              )}

              <TableCell>
                {/* R16 W1/W2: erste Zelle, wenn die Produktspalte FEHLT — auf
                    `/products/:sku` ist die Liste auf ein Produkt gefiltert und die
                    Produktspalte entfaellt. Dann traegt die Marktzelle den
                    Statuscluster, damit Zustand, Warnsymbol und Fehlschlaege nicht
                    mit der Spalte verschwinden. In der Vollansicht unveraendert. */}
                {showProductCol ? renderMarket(s.market) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {embeddedStatus}
                    {renderMarket(s.market)}
                  </Box>
                )}
              </TableCell>

              {showCompetitorCol && (
                <TableCell>
                  <MuiLink component={RouterLink} to={`/competitors/${s.competitorId}`} underline="hover">
                    {s.competitorName}
                  </MuiLink>
                </TableCell>
              )}

              <TableCell align="right">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                  {s.hasSale && (
                    <Chip label="Sale" color="error" size="small" icon={<SellIcon />} variant="outlined" />
                  )}
                  {typeof s.totalPrice === "number" ? (
                    <Tooltip title={tooltipTitle}>
                      <Box component="span">{fmtCompPrice(s.totalPrice)}</Box>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </Box>
              </TableCell>

              {showPriceDifference && (
                <TableCell align="right">
                  {s.priceDifference !== null ? (
                    <Typography variant="body2" color={s.priceDifference < 0 ? "success.main" : "error.main"}>
                      {s.priceDifference > 0 ? "↑" : "↓"} {fmtCompPrice(Math.abs(s.priceDifference))}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}

              {showFailuresCol && (
              <TableCell align="right">
                {s.failedScrapesInARow > 0 ? (
                  <Tooltip
                    title={`${s.failedScrapesInARow} consecutive failures \u00b7 ${s.failedScrapesTotal} in total`}
                  >
                    <Box component="span">
                      <Pill tone="err">{s.failedScrapesInARow}× failed</Pill>
                    </Box>
                  </Tooltip>
                ) : s.failedScrapesTotal > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {s.failedScrapesTotal}× total
                  </Typography>
                ) : (
                  "—"
                )}
              </TableCell>
              )}

              {showStockStatus && (
                <TableCell>
                  {s.availability === false && (
                    <Chip label="Out of stock" size="small" variant="outlined" sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main })} />
                  )}
                </TableCell>
              )}

              <TableCell align="center">
                {isEmbedded ? (
                  // 🔴 §2.1/B9: die eingebettete Fassung behaelt ihr heutiges ⋮
                  // samt Menue, zeichengleich (G15 fasst die Einbettung an).
                  <>
                    <IconButton size="small" onClick={(e) => openMenu(e, s.id)}
                      aria-label={`Actions for ${s.sku}`}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu anchorEl={menuAnchorEl} open={menuRowId === s.id} onClose={closeMenu}>
                      <MenuItem
                        onClick={() => scrapeAndPersist(s)}
                        sx={{ gap: 1 }}
                      >
                        <PlayArrowIcon fontSize="small" />
                        Scrape &amp; persist
                      </MenuItem>
                      <MenuItem
                        component={RouterLink}
                        to={`/scrapers/${s.id}/edit`}
                        onClick={closeMenu}
                        sx={{ gap: 1 }}
                      >
                        Edit scraper config
                      </MenuItem>
                      {s.productUrl && (
                        <MenuItem
                          component="a"
                          href={s.productUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMenu}
                          sx={{ gap: 1 }}
                        >
                          <OpenInNewIcon fontSize="small" />
                          Open product URL
                        </MenuItem>
                      )}
                      <MenuItem component={RouterLink} to={`/products/${s.sku}`} onClick={closeMenu}>
                        View product
                      </MenuItem>
                      {s.isActive ? (
                        <MenuItem onClick={() => deactivateAsk(s.id)}>Deactivate…</MenuItem>
                      ) : (
                        <MenuItem onClick={() => activate(s.id)}>Activate</MenuItem>
                      )}
                    </Menu>
                  </>
                ) : (
                  // 🔴 §2.6/B5/P19: `RowActionsMenu` (BS18) statt handgerolltem
                  // Menue. Der Name traegt den GANZEN Zeilenbezug — Produktname,
                  // Variante UND SKU: `messeP19` vergleicht gegen die
                  // TEXTKNOTEN der Kennungsspalte, und die Produktzelle traegt
                  // zwei („Name — Variante" und „(SKU)"). „Actions for <SKU>"
                  // allein enthielte den ersten nicht und waere rot.
                  <RowActionsMenu
                    label={zeilenAktionsName(s)}
                    actions={zeilenAktionen(s)}
                  />
                )}
              </TableCell>
            </TableRow>
          );
        })}

        {sorted.length === 0 && (
          <TableRow>
            <TableCell colSpan={columnCount} sx={{ py: 4 }}>
              {leerzustand}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  // 🔴 §2.7/N11.1: unter `sm` ersetzt die Karte die Tabellenzeile — dieselben
  // Felder, untereinander statt nebeneinander. Bei 390 px ist eine Tabelle eine
  // waagerechte Rollfläche, und die Aktionsspalte stünde ausserhalb des Bildes.
  //
  // 🔴 **N11.3 — nichts geht verloren, nichts kommt hinzu:** jedes Kartenfeld
  // steht auch in der Desktop-Zeile. „Last scraped" ist KEINE Spalte und darf
  // deshalb NICHT auf die Karte (der Status-Chip traegt seine Aussage). Die
  // Zahl der Karten = Zeilen der Seite (P15).
  const karten = (
    // 🔴 Hausform `SecondLifeBestand.jsx:2244-2245`: die Karten stehen mit
    // `SP.card` Abstand, nicht bündig — `Panel` traegt nur den Innenabstand.
    <Stack spacing={SP.card}>
      {pageRows.map((s) => {
        const currencyCode = marketsByCode[s.marketCode?.toUpperCase()]?.currency_code ?? "EUR";
        return (
          <MobileRecordCard
            key={s.id}
            data-testid={`karte-${s.id}`}
            title={
              // 🔴 R16 B2/B3/W4 (Runde 1): der Titel ist das EINZIGE
              // Bedienelement der Karte (`MobileRecordCard` §@dontUseFor — die
              // Flaeche traegt keinen `onClick`). Deshalb: (a) der Haus-Boden
              // `KARTE_TITEL_BASIS_SX` (44 px auf grobem Zeiger, als `::after`-
              // FLAECHE am `Link`), (b) die Zwei-Zeilen-Klemme am INNEREN
              // `<span>` (`TITEL_KLEMME_SX`), (c) sichtbare Affordanz
              // (`underline="hover"`, Hausform `ProductsTable.jsx:2119`). Bis zur
              // Nachbesserung war es eine `Typography` ohne Klemme, ohne Boden
              // und mit `textDecoration: "none"` — auf dem Telefon eine
              // Sackgasse, die man nur durch Zufall oeffnete.
              <MuiLink
                variant="body2"
                component={RouterLink}
                to={`/scrapers/${s.id}/edit`}
                underline="hover"
                color="text.primary"
                sx={KARTE_TITEL_BASIS_SX}
              >
                <Box
                  component="span"
                  data-testid={`karte-titel-klemme-${s.id}`}
                  sx={TITEL_KLEMME_SX}
                >
                  {s.productName} — {s.productVariant}
                </Box>
              </MuiLink>
            }
            // 🔴 N11.5: die Kennung wird NIE gekuerzt — der `id`-Slot des
            // Bausteins traegt `nowrap` und keine Ellipse.
            id={s.sku}
            fieldTestIds={{
              title: `karte-titel-${s.id}`,
              id: `karte-kennung-${s.id}`,
              chips: `karte-chips-${s.id}`,
              meta: `karte-meta-${s.id}`,
            }}
            chips={
              <>
                {/* 🔴 Höchstens ZWEI Chips (§2.7): Status + Fehlschlaege. Die
                    Marke `karte-status-<id>` ist der Gegenstand von P22 mobil
                    (`SEL.kartenStatus`). */}
                <Pill
                  tone={statusTon(s)}
                  title={statusGrund(s)}
                  data-testid={`karte-status-${s.id}`}
                >
                  {statusWort(s)}
                </Pill>
                {s.failedScrapesInARow > 0 && (
                  <Pill tone="err">{`${s.failedScrapesInARow}× failed`}</Pill>
                )}
              </>
            }
            meta={
              // 🔴 Drei Felder, Werte textgleich mit der Desktop-Zeile —
              // dieselbe `renderMarket`- und `preisText`-Form.
              <Typography variant="caption" color="text.secondary">
                {`${renderMarket(s.market)} · ${s.competitorName} · ${preisText(s.totalPrice, currencyCode) ?? "—"}`}
              </Typography>
            }
            // 🔴 R16 W1 (Runde 1): N11.2/§2.7 — Titel → Kennung → CHIPS → meta.
            // Die Vorgabe des Bausteins ist `meta-first`; hier wird gedreht, weil
            // der Status die Aussage ist, nach der man die Liste liest (Hausform
            // `ProductsTable`).
            slotOrder="chips-first"
            actions={
              // 🔴 §2.7: „⋮ mit demselben Menue" — EIN Aktionssatz, zwei
              // Traeger. Der Name traegt denselben Zeilenbezug wie die Zeile.
              // 🔴 R16 B3 (Runde 1): auf der Karte ist das ⋮ neben dem Titel der
              // zweite Weg — und der einzige gestenfreie. Es traegt deshalb auf
              // grobem Zeiger den 44-px-Boden in BEIDEN Achsen
              // (`TOUCH_TARGET_COARSE_QUADRAT`; `minHeight` allein machte aus dem
              // runden Knopf ein Oval). P14 sieht es nicht: dessen Huellen-Regel
              // nimmt jeden Vorfahren ≥ 44 — und das ist das `Panel` der Karte.
              <RowActionsMenu
                label={zeilenAktionsName(s)}
                actions={zeilenAktionen(s)}
                sx={TOUCH_TARGET_COARSE_QUADRAT}
              />
            }
          />
        );
      })}
    </Stack>
  );

  // 🔴 G10b (§2.5): **eine** Leiste statt zwei handgerollter Zeilen und einer
  // mobilen Sonderzeile. Z1 = Suche · zwei Facetten (Competitors, Markets) ·
  // „Filters"-Öffner · Zähler · Auffrischen; Z2 = zwei Pillenreihen (Achse +
  // Bedingungen); Z3 = `ActiveFilterBar` aus `query.chips`.
  const pillenZahlen = dashboard?.pillenZahlen;
  const bedingungenAktiv = quickWerte;
  const aktivitaetsWert = aktiverModus === "all" ? null : aktiverModus;
  // Der Blatt-Zähler ist die TREFFERMENGE (`sorted.length`), nicht die
  // Seitenlaenge — und bei Sperre `undefined` (R4-W4: EINE Regel, beide
  // Breiten; `FilterSheet` rendert den Knopf dann nicht).
  const blattZaehler = sperre ? undefined : sorted.length;
  const blattKriterien = isMobile
    ? [
        {
          key: "competitor",
          kind: "select",
          label: "Competitors",
          multiple: true,
          value: state.competitor ?? LEER_LISTE,
          onChange: (v) => set("competitor", v),
          options: (competitors || []).map((c) => ({ value: String(c.id), label: c.name || String(c.id) })),
          emptyLabel: "Any",
        },
        {
          key: "market",
          kind: "select",
          label: "Markets",
          multiple: true,
          value: state.market ?? LEER_LISTE,
          onChange: (v) => set("market", v),
          options: marketOptions.map((m) => ({ value: String(m.id), label: m.label })),
          emptyLabel: "Any",
        },
      ]
    : [
        // 🔴 B11: der Öffner heisst in BEIDEN Breiten „Filters" und trägt am
        // Rechner das Product-Kriterium — es schreibt `q` (eine SKU), nicht
        // eine eigene Dimension (§3.1). Der leere Wert zeigt „Any" (N11.14).
        {
          key: "product",
          kind: "select",
          label: "Product",
          value: productNameMap[searchText.trim()] ? searchText.trim() : "",
          onChange: (v) => set("q", v),
          options: productOptions.map((p) => ({ value: p.id, label: p.name })),
          emptyLabel: "Any",
        },
      ];
  const blattAktiv = isMobile
    ? [(state.competitor ?? []).length > 0, (state.market ?? []).length > 0].filter(Boolean).length
    : (productNameMap[searchText.trim()] ? 1 : 0);

  const toolbarBlock = renderFilters ? (
    <ListToolbar
      // 🔴 G0.3: der Kurzbefehl ist Einzelinstanz — die Leiste verwaltet die
      // Warteliste selbst.
      shortcut="/"
      search={{
        value: searchText,
        onChange: (v) => set("q", v),
        onCommit: (v) => set("q", v),
        // 🔴 N7.4/P5: EIN Text in beiden Breiten. `label` ist derselbe Text
        // ohne die Auslassungspunkte — der zugängliche Name beginnt mit
        // „Search" (P5), `inputRole` gibt dem Feld die Rolle `searchbox`.
        placeholder: "Search product, variant or SKU…",
        label: "Search product, variant or SKU",
        inputRole: "searchbox",
        disabled: sperre,
        sx: { flex: 1, minWidth: 0 },
      }}
      // 🔴 Genau ZWEI Facetten (die Leiste wirft bei > 3) — Product ist KEINE
      // Facette (W6/B11: 149 SKUs → Suche, §3.1 Regel 3).
      facets={[
        <BottomSheetMultiSelect
          key="competitor"
          desktopForm="pill"
          label="Competitors"
          value={state.competitor ?? LEER_LISTE}
          onChange={(v) => set("competitor", v)}
          options={(competitors || []).map((c) => ({ value: String(c.id), label: c.name || String(c.id) }))}
          disabled={sperre}
        />,
        <BottomSheetMultiSelect
          key="market"
          desktopForm="pill"
          label="Markets"
          value={state.market ?? LEER_LISTE}
          onChange={(v) => set("market", v)}
          options={marketOptions.map((m) => ({ value: String(m.id), label: m.label }))}
          disabled={sperre}
        />,
      ]}
      moreFilters={{
        disabled: sperre,
        // 🔴 Dieselben Kriterien wie die Z1-Facetten, je Breite (§2.5). Ohne
        // den Eintrag wären Competitors/Markets mobil unerreichbar (die Leiste
        // rendert Facetten nie inline unter `sm`, N11.8).
        criteria: blattKriterien,
        // 🔴 B1 (Regel-16 Runde 1, Fläche): der Sortier-Abschnitt gehoert in
        // das Blatt — er ist dessen ERSTER Abschnitt (N11.14), und auf dem
        // Telefon ist er ab G10c der EINZIGE Sortierzugang (der Tabellenkopf
        // faellt mit den Karten weg). Hausform wie `ProductsTable.jsx:1960`.
        // 🔴 Seit G10c traegt er auch die RICHTUNG (je Feld ein Paar) — vorher
        // setzte er nur das Feld aufsteigend, und mobil war die Richtung damit
        // gar nicht erreichbar (G10b-UX W2-R2).
        sort: {
          // 🔴 G10c (Orchestrator-Entscheid aus W2-R2): mobil faellt der
          // sortierbare Tabellenkopf mit den Karten weg — die RICHTUNG wird
          // deshalb HIER waehlbar, je Feld ein Paar. Der Wert ist der
          // VORZEICHENBEHAFTETE `sort`-Wert (`-feld` = absteigend, §3.1), und
          // `value` ist derselbe vorzeichenbehaftete Wert: angekreuzt ist genau
          // der aktuelle Stand. Hausform `SecondLifeBestand.jsx:2020-2031`.
          value: sortWert,
          onChange: (v) => set("sort", v),
          options: [
            { value: "competitorName", label: "Competitor (A–Z)" },
            { value: "-competitorName", label: "Competitor (Z–A)" },
            { value: "productName", label: "Product / Variant / SKU (A–Z)" },
            { value: "-productName", label: "Product / Variant / SKU (Z–A)" },
            { value: "market", label: "Market (A–Z)" },
            { value: "-market", label: "Market (Z–A)" },
            { value: "totalPrice", label: "Last Price (low to high)" },
            { value: "-totalPrice", label: "Last Price (high to low)" },
            { value: "priceDifference", label: "Diff. to Us (low to high)" },
            { value: "-priceDifference", label: "Diff. to Us (high to low)" },
            { value: "failedScrapesInARow", label: "Failures (fewest first)" },
            { value: "-failedScrapesInARow", label: "Failures (most first)" },
          ],
        },
        // 🔴 `resultCount` ist ein `number|null|undefined` — beide Breiten
        // dieselbe Regel (R4-W4/B14).
        resultCount: blattZaehler,
        activeCount: blattAktiv,
        onClearAll: clearAll,
      }}
      // 🔴 Z2 — ZWEI Reihen, ausdrücklich (B2): eine Achse (Einfachauswahl)
      // und die fünf Bedingungen (Mehrfach, UND-verknüpft). `FilterPillRow`
      // trägt EIN `value` und EIN `multi` für die ganze Reihe — eine Reihe
      // kann beides nicht zugleich tragen.
      quickFilters={
        <Stack spacing={SP.tight}>
          <FilterPillRow
            alwaysShow
            multi={false}
            value={aktivitaetsWert}
            onChange={(v) => set("active", [v])}
            disabled={sperre}
            options={[
              { value: "only", label: "Active only", count: pillenZahlen?.active },
              { value: "inactive", label: "Inactive", count: pillenZahlen?.inactive },
            ]}
          />
          <FilterPillRow
            alwaysShow
            multi
            label="Conditions"
            semantics="and"
            value={bedingungenAktiv}
            onChange={(v) => {
              // 🔴 W10: die Page-gone-Pille schaltet die Vorgabe „nur aktive"
              // AUS — sonst wäre die Schnittmenge leer, obwohl der Bediener
              // nach den Kandidaten sucht. Zwei `set`-Aufrufe in EINEM Handler
              // sind zulässig: der Hook fängt den ersten mit dem Ref ein.
              if (v.includes("pageGone") && !bedingungenAktiv.includes("pageGone") && aktiverModus === "only") {
                set("active", []);
              }
              set("quick", v);
            }}
            disabled={sperre}
            options={BEDINGUNGEN.map((b) => ({ ...b, count: pillenZahlen?.[b.value] }))}
          />
        </Stack>
      }
      // 🔴 EIN Zähler, „N of M targets" — Desktop in Z1, mobil in Z3.
      count={sperre ? undefined : { noun: "targets", n: sorted.length, m: scrapers.length }}
      refresh={<RefreshButton ariaLabel="Refresh scraper targets" onClick={fetchConfigs} />}
      query={{ chips, clearAll }}
    >
      {/* 🔴 N10.4/P13 — ein UNBEKANNTER Wert in der Adresse wird VERWORFEN und
          EINMAL gemeldet (B5: der Hinweis muss im Baum stehen). */}
      {ignoredValues.length > 0 && (
        <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.card }}>
          {`Ignored unknown filter value “${ignoredValues[0].value}”.`}
        </Alert>
      )}
    </ListToolbar>
  ) : null;

  const donutItems = dashboard
    ? dashboard.distribution.map((item) => ({
        name: item.name,
        value: item.value,
        meta:
          dashboard.totalSkus > 0 && item.value > 0
            ? formatPercent(item.value / dashboard.totalSkus, { digits: 1 })
            : undefined,
      }))
    : [];
  const coverageTop = dashboard
    ? dashboard.coverage.filter((c) => c.skuCount > 0).slice(0, 5)
    : [];
  const coverageMax = coverageTop.reduce((max, entry) => Math.max(max, entry.skuCount || 0), 0) || 1;
  // 🔴 §2.3: EINE KPI-Reihe. Die vier Kacheln rechnet das `useMemo` aus dem
  // UNGEFILTERTEN Bestand (N6.2); kein `onClick` (N6.1).
  const dashboardContent = !tableOnly && !isEmbedded && dashboard ? (
    <Box data-testid="liste-kpi">
      <KpiRow>
        {dashboard.kpiTiles.map((stat) => (
          <KpiCell key={stat.label}>
            <StatTile label={stat.label} value={stat.value} hint={stat.secondary} labelTooltip={stat.hint} />
          </KpiCell>
        ))}
      </KpiRow>
    </Box>
  ) : null;

  // 🔴 §2.4: die Analyseflaeche ist EINGEKLAPPT und steht UNTER dem Panel. Ihre
  // `summary` traegt die Zahlen selbst — „Inactive" und „Missing price" haben
  // ihren Zaehler erst mit den Quick-Pillen (G10b), „Products without scrapers"
  // stand heute sichtbar auf einer Karte. Kein Zwischenstand ohne Zahl.
  // 🔴 W36/R4-W1: solange `trackedProducts === null` ist, rendert die dritte
  // Zahl `—` (UNBEKANNT), nicht 0.
  const uncoveredLabel =
    dashboard && dashboard.uncoveredCount !== null
      ? formatInt(dashboard.uncoveredCount)
      : "—";
  const inactiveLabel = dashboard ? formatInt(dashboard.inactiveCount) : "—";
  const missingPriceLabel = dashboard ? formatInt(dashboard.missingPriceCount) : "—";
  const analysisSummary = dashboard
    ? `Targets per product · Market coverage · Inactive ${inactiveLabel} · Missing price ${missingPriceLabel} · Products without scrapers ${uncoveredLabel}`
    : "";
  // 🔴 G10a-Nachbesserung (B-1): `CollapsibleSection` blendet die `summary` unter
  // `sm` aus (`CollapsibleSection.jsx:122`, R16 W2) — am Telefon haetten die drei
  // Zahlen damit bis G10b GAR KEINEN Ort. Deshalb steht ueber der Sektion eine
  // eigene Zeile, nur mobil, mit denselben Zahlen. Sie liegt AUSSERHALB der
  // Sektion: die ist `defaultExpanded={false}`, im Sektionskoerper waere die Zeile
  // zugeklappt und damit unsichtbar (Plan §2.4 „sichtbar ohne Aufklappen").
  const analysisMobileSummary = dashboard
    ? `Inactive ${inactiveLabel} · Missing price ${missingPriceLabel} · Products without scrapers ${uncoveredLabel}`
    : "";

  const analysisMobileLine = !tableOnly && !isEmbedded && dashboard ? (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="analyse-zahlen-mobil"
      sx={{ display: { xs: "block", sm: "none" } }}
    >
      {analysisMobileSummary}
    </Typography>
  ) : null;

  const analysisSection = !tableOnly && !isEmbedded && dashboard ? (
    <CollapsibleSection
      title="Analysis"
      ariaLabel="Analysis — charts and stock figures"
      defaultExpanded={false}
      summary={analysisSummary}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "2fr 2fr" },
            gap: 2,
          }}
        >
          {/* 🔴 E9-Nachzug (2026-10-07): `<Panel>` traegt die Karte, `DonutCard`
              zeichnet mit `bare` NUR den Inhalt — genau die Bauform, fuer die
              `bare` da ist. */}
          <Panel>
            <DonutCard bare label="Targets per product" items={donutItems} />
          </Panel>
          <Panel>
            <Typography variant="subtitle2">Market coverage</Typography>
            {coverageTop.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No markets configured yet.
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {coverageTop.map((entry) => (
                  <Box key={entry.market}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {entry.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.skuCount} products
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (entry.skuCount / coverageMax) * 100)}
                      sx={{ mt: 0.5, height: 6, borderRadius: 999 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {entry.targetCount} targets • {entry.activeTargetCount} active
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Panel>
        </Box>
        {/* Die drei Zahlen, die die KPI-Leiste verlassen haben (§2.3, W24) —
            als Textzeilen, Zahl + Beschriftung, keine Kachel. */}
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: ANALYSE_LABEL_MIN_WIDTH } }}>
              Markets tracked
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatInt(dashboard.marketsWithCoverage)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({formatInt(dashboard.marketsWithThreePlus)} with ≥3 targets)
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: ANALYSE_LABEL_MIN_WIDTH } }}>
              Avg targets per product
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatNumber(dashboard.avgTargets || 0, { digits: 1 })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({formatInt(dashboard.totalSkus)} SKUs)
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: ANALYSE_LABEL_MIN_WIDTH } }}>
              Products with ≥3 targets
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatInt(dashboard.skuWithThreePlus)}
            </Typography>
          </Stack>
        </Stack>
        {/* Der Einstieg in den Dialog ist ein KNOPF in der Sektion; seine Zahl
            steht in der `summary` (§2.4). Der Dialog selbst bleibt
            zeichengleich. */}
        <Box>
          {/* 🔴 G10a-Nachbesserung (B-2): solange die Produktliste nicht
              geantwortet hat, ist der Bestand UNBEKANNT — der Knopf oeffnet
              dann keinen Dialog, der „Every tracked product already has a
              scraper target." behauptete. Gesperrt mit sichtbarem Grund und
              `aria-describedby` (Hausform `OpenCases.jsx:703-717`); `aria-disabled`
              statt `disabled`, weil ein deaktivierter Knopf nicht fokussierbar
              ist und der Grund damit nur im Browsemodus erreichbar waere. */}
          <Button
            size="small"
            variant="outlined"
            aria-disabled={dashboard.uncoveredCount === null || undefined}
            aria-describedby={
              dashboard.uncoveredCount === null ? missingSkuReasonId : undefined
            }
            onClick={
              dashboard.uncoveredCount === null
                ? undefined
                : () =>
                    setMissingSkuModal({
                      open: true,
                      items: dashboard.uncovered,
                    })
            }
            sx={{
              "&[aria-disabled=\"true\"]": {
                color: "text.disabled",
                borderColor: "action.disabledBackground",
              },
            }}
          >
            Products without scrapers
          </Button>
          {dashboard.uncoveredCount === null && (
            // 🔴 R16 B-1: der Grund sagt den WAHREN Zustand. „has not loaded
            // yet" war nach einem gescheiterten Abruf eine falsche Behauptung
            // (es lud nichts mehr) und liess den Leser ohne naechsten Schritt.
            <Box sx={{ mt: 0.5 }}>
              <Typography
                id={missingSkuReasonId}
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {productLoad === "fehler"
                  ? "Product list could not be loaded."
                  : "Product list is still loading."}
              </Typography>
              {productLoad === "fehler" && (
                <Button size="small" onClick={retryAll} sx={{ mt: 0.5 }}>
                  Try again
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Stack>
    </CollapsibleSection>
  ) : null;

  const tablePaper = !isEmbedded && !tableOnly ? (
    <>
      <Panel data-testid="liste-panel">
        {/* 🔴 N8.5/§2.8: der Grund der Sperre steht als SICHTBARER Text DIREKT
            ueber der Leiste, im selben Panel — mit der Haus-Marke
            `data-testid="erster-aufbau-gesperrt"`. `messeP9Sperre` liest genau
            sie und verlangt den Satz `/locked until the list loads/i`. */}
        {/* 🔴 W1 (Regel-16 Runde 1): der Sperrhinweis ist eine ZUSTANDSNOTIZ,
            keine Unterbrechung. MUI setzt an `Alert` sonst `role="alert"`
            (assertiv) — und im Fehlerfall stehen dann zwei assertive Regionen
            nebeneinander, deren zweite die Ursache nennt und die erste nur die
            Folge. `role="status"` stuft die Dringlichkeit ab, der Wortlaut
            bleibt W16/B15-konform. */}
        {sperre && (
          <Alert
            severity="warning"
            role="status"
            data-testid="erster-aufbau-gesperrt"
            sx={{ mb: SP.card }}
          >
            Search and filters are locked until the list loads. Use Try again below.
          </Alert>
        )}
        {toolbarBlock}
        {/* 🔴 B10/G10c: `liste-tabelle` und `liste-karte` sind GEGENSTÜCKE —
            am Rechner traegt die Zone die Tabelle, am Telefon die Karten. Die
            Marke aus G10a (damals an derselben Tabelle) sitzt jetzt am
            Kartenbaum, wie B10 es vorgesehen hat.
            🔴 W30: der `ref` misst die ROHE Behaelterbreite für
            `TableViewport` — gemessen wird der RAUM, nicht die gedeckelte
            Tabelle. 🔴 Der Rollkasten ist am Rechner `TableViewport` selbst;
            die `overflowX`-Hülle bleibt nur der eingebetteten Fassung (die
            rendert hier nicht, `tablePaper` gilt nur im Seitenmodus — der
            Zweig steht für die Lesbarkeit des geteilten `table`-Baums). */}
        <Box
          ref={tabelleRef}
          data-testid={isMobile ? "liste-karte" : "liste-tabelle"}
          sx={{ width: "100%", mt: toolbarBlock ? 2 : 0 }}
        >
          {isMobile ? (
            sorted.length === 0 ? (
              // 🔴 §2.8: am Telefon gibt es keinen Tabellenkoerper — der
              // Leerzustand steht in der Kartenzone. Derselbe Knoten wie am
              // Rechner (`leerzustand`), EIN Objekt.
              <Box sx={{ py: 4 }}>{leerzustand}</Box>
            ) : (
              karten
            )
          ) : seitenModus ? (
            <TableViewport
              footerPadPx={0}
              priorities={spalten}
              containerWidth={behaelterBreite}
              // 🔴 R16 B1 (Runde 2): `columns` schaltet `table-layout: fixed` und
              // setzt die Breiten aus `COLUMN_MAX[kind]` — der Kopf springt
              // dadurch nicht mehr zwischen befuelltem und leerem Zustand.
              // Nur der Seitenmodus kommt hierher (B9).
              columns={spaltenBreiten}
            >
              {table}
            </TableViewport>
          ) : (
            // 🔴 R15 W3 (Runde 1): dieser Zweig ist HEUTE UNERREICHBAR und
            // bleibt als dokumentierte B9-Weiche stehen. `seitenModus =
            // !isEmbedded && !tableOnly` (`:333`), und `tablePaper` wird nur
            // unter genau dieser Bedingung gerendert (`:2342`) — hier ist
            // `seitenModus` also immer wahr. Der Zweig steht fuer einen
            // kuenftigen Seitenmodus, der die Tabelle OHNE `TableViewport`
            // zeichnet (heute gibt es keinen); keine Probe kann ihn erreichen.
            // Wer ihn belebt, muss P33 (Kopfzellen-Stand) fuer ihn mitmessen.
            table
          )}
        </Box>
      </Panel>
      {/* 🔴 R16 W1 / N8.7 (Hausform `POOverview.jsx:1833`): die Marke steht nur
          mit Inhalt. `TableFooterSimple` gibt bei `count === 0` `null` zurueck —
          ohne die Bedingung bliebe ein LEERER `liste-fuss`-Kasten stehen, und
          P33 (G10b) prueft „Marke fehlt ODER traegt Text". */}
      {resolvedFooterMode !== "none" && sorted.length > 0 && (
        <Box data-testid="liste-fuss">
          <TableFooterSimple
            count={sorted.length}
            page={seiteAktiv}
            rowsPerPage={rowsPerPage}
            // 🔴 B3: der Hook führt `page` 1-basiert, der Footer 0-basiert —
            // die Umrechnung steht EINMAL oben im JSX (`seiteAktiv`).
            onPageChange={(p) => set("page", p + 1)}
            maxWidth="100%"
            // 🔴 F5-1 (UI-Durchgang 2 Teil 5, 2026-10-09): mobil steht die
            // Blätterleiste im FLUSS statt zu schweben — dieselbe Begründung wie
            // auf `/products` und `/vendors` (`common/TableFooterSimple.jsx`,
            // Teil 4). Ab `sm` bleibt sie schwebend.
            stickyOnMobile={false}
          />
        </Box>
      )}
    </>
  ) : null;

  const embeddedFooter = resolvedFooterMode === "inline" && needsPagination;

  const embeddedAddHandler =
    typeof onAddButtonClick === "function" ? onAddButtonClick : handleAddScraper;
  const showEmbeddedAddButton = tableOnly || addButtonInEmbedded;

  const embeddedSection = isEmbedded ? (
    <Box sx={{ px: tableOnly ? 0 : 3, width: "100%" }}>
      {showEmbeddedAddButton && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={embeddedAddHandler}
            startIcon={<AddIcon fontSize="small" />}
          >
            {addButtonLabel}
          </Button>
        </Box>
      )}
      <Box sx={{ width: "100%", overflowX: "auto" }}>{table}</Box>
      {embeddedFooter && (
        <Box sx={{ px: 2, py: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={sorted.length}
            page={embeddedPage}
            onPageChange={(e, p) => setEmbeddedPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setEmbeddedPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}
    </Box>
  ) : null;

  const dialogs = (
    <>
      {/* ── Inline scrape & persist dialog ── */}
      <Dialog open={scrapeDialog.open} onClose={closeScrapeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          Scrape &amp; persist
          {scrapeDialog.target && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {scrapeDialog.target.productName || scrapeDialog.target.sku}
              {' · '}
              {scrapeDialog.target.competitorName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <ProgressStream {...scrape.state} />
        </DialogContent>
        <DialogActions>
          {scrapeDialog.target && (
            <Button
              size="small"
              component={RouterLink}
              to={`/scrapers/${scrapeDialog.target.id}/edit`}
              onClick={closeScrapeDialog}
              sx={{ mr: 'auto' }}
            >
              Open config
            </Button>
          )}
          {/* Always closable — closeScrapeDialog stops the stream first, so this
              doubles as Cancel while a scrape is in flight (never trap the user). */}
          <Button onClick={closeScrapeDialog} variant={scrape.state.status === 'running' ? 'outlined' : 'contained'} size="small">
            {scrape.state.status === 'running' ? 'Cancel' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpenState} onClose={() => setConfirmOpenState(false)}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText>This target will no longer be scraped routinely.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpenState(false)}>Cancel</Button>
          <Button onClick={deactivate} autoFocus color="error">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={maintenanceDialog.open} onClose={closeMaintenanceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {maintenanceDialog.mode === "dedupe" ? "Deduplication summary" : "Duplicate preview"}
        </DialogTitle>
        <DialogContent dividers>
          {maintenanceDialog.summary ? (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Duplicate groups: <strong>{maintenanceDialog.summary.total_groups ?? 0}</strong>
              </Typography>
              {typeof maintenanceDialog.summary.remapped_rows === "number" && (
                <Typography variant="body2" color="text.secondary">
                  Remapped rows: {maintenanceDialog.summary.remapped_rows}
                </Typography>
              )}
              {typeof maintenanceDialog.summary.deactivated_targets === "number" && (
                <Typography variant="body2" color="text.secondary">
                  Deactivated targets: {maintenanceDialog.summary.deactivated_targets}
                </Typography>
              )}
              <Stack spacing={1} sx={{ maxHeight: 260, overflowY: "auto" }}>
                {(maintenanceDialog.summary.groups || []).slice(0, 12).map((g) => (
                  <Box key={`${g.sku}-${g.market}-${g.competitor_id}`}>
                    <Typography variant="body2" fontWeight={600}>
                      {g.sku} • {g.market} • {g.competitor_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Keep {g.keep} — duplicates {g.dups?.length || 0}
                    </Typography>
                  </Box>
                ))}
                {(maintenanceDialog.summary.groups || []).length > 12 && (
                  <Typography variant="caption" color="text.secondary">
                    +{(maintenanceDialog.summary.groups || []).length - 12} more groups
                  </Typography>
                )}
              </Stack>
              {(maintenanceDialog.summary.groups || []).length === 0 && (
                <Typography variant="body2">No duplicates found.</Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2">No data.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMaintenanceDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dedupeConfirmOpen} onClose={() => setDedupeConfirmOpen(false)}>
        <DialogTitle>Deduplicate & consolidate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will keep a single target per SKU / market / competitor, remap historic prices, and deactivate duplicates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDedupeConfirmOpen(false)}>Cancel</Button>
          <Button onClick={runDedupe} color="error" disabled={maintenanceLoading}>
            Run dedupe
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={competitorModalOpen} onClose={() => setCompetitorModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Selected competitors</DialogTitle>
        <DialogContent dividers>
          {(state.competitor ?? LEER_LISTE).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              All competitors are included.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(state.competitor ?? LEER_LISTE).map((id) => (
                <Chip
                  key={id}
                  label={competitorNameMap[id] || id}
                  onDelete={() =>
                    set(
                      "competitor",
                      (state.competitor ?? LEER_LISTE).filter((value) => value !== id),
                    )
                  }
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              set("competitor", []);
              setCompetitorModalOpen(false);
            }}
          >
            Show all
          </Button>
          <Button onClick={() => setCompetitorModalOpen(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={missingSkuModal.open} onClose={() => setMissingSkuModal({ open: false, items: [] })} maxWidth="sm" fullWidth>
        <DialogTitle>Products without scrapers</DialogTitle>
        <DialogContent dividers>
          {missingSkuModal.items.length ? (
            <Stack spacing={1}>
              {missingSkuModal.items.slice(0, 25).map((p) => (
                <Box key={p.sku} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <MuiLink
                    component={RouterLink}
                    to={`/products/${p.sku}`}
                    underline="hover"
                    color="inherit"
                    sx={{ fontSize: 13 }}
                  >
                    {p.name}
                  </MuiLink>
                  <Typography variant="caption" color="text.secondary">
                    {p.sku}
                  </Typography>
                </Box>
              ))}
              {missingSkuModal.items.length > 25 && (
                <Typography variant="caption" color="text.secondary">
                  +{missingSkuModal.items.length - 25} more
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Every tracked product already has a scraper target.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingSkuModal({ open: false, items: [] })}>Close</Button>
        </DialogActions>
      </Dialog>

    </>
  );

  if (!isEmbedded) {
    return (
      <>
        <PageContainer width="full">
          <Box data-testid="liste-kopf">
            <ViewHeader
              title="Scraper Targets"
              breadcrumbs={
                showBreadcrumbs
                  ? [
                      { label: "Home", href: "/" },
                      { label: "Scraper Targets" },
                    ]
                  : []
              }
              actions={headerActions}
              // R15 B-2: EIN Block fuer Lade- und geladenen Zustand — sonst
              // braeche die Aktionszeile beim Eintreffen der Daten anders um.
              overflow={headerOverflow}
              rightAdornment={headerRightAdornment}
            />
          </Box>
          <Stack spacing={3} sx={{ pb: 6 }}>
            {loadError && !sperre && (
              <Alert severity="warning" variant="outlined">
                Some lists could not be loaded. Market names may be missing in
                the table and may show as raw codes in the coverage panel below.
              </Alert>
            )}
            {dashboardContent}
            {tablePaper}
            {/* §2.4: die Analyse steht UNTER dem Panel (Kopf → KPI → Panel →
                Fusszeile → Analysis). Die mobile Zahlenzeile steht UEBER der
                Sektion, weil deren `summary` unter `sm` ausgeblendet ist und
                die Sektion zugeklappt startet (B-1, Plan §2.4). */}
            {analysisMobileLine}
            {analysisSection}
          </Stack>
        </PageContainer>
        {dialogs}
      </>
    );
  }

  return (
    <>
      {embeddedSection}
      {dialogs}
    </>
  );
}

export function ScraperTargetsTable({
  addButtonLabel = "Add scraper",
  addButtonInEmbedded = true,
  ...rest
}) {
  return (
    <ScraperList
      variant="embedded"
      showFilters={false}
      showBreadcrumbs={false}
      footerMode="inline"
      tableOnly
      addButtonInEmbedded={addButtonInEmbedded}
      addButtonLabel={addButtonLabel}
      {...rest}
    />
  );
}
